import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { MapContainer, TileLayer, Polyline, useMap, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import confetti from "canvas-confetti";
import { 
  Play, Square, Pause, MapPin, Clock, Zap, TrendingUp, History,
  User, Settings, LogOut, LogIn, Mail, Lock, Eye, EyeOff, Loader2,
  ChevronRight, ChevronLeft, Target, Award, Calendar, Timer, Navigation, X,
  Home, Trash2, AlertCircle, Share2, Trophy, Star, Flame,
  Bell, Heart, Medal, Crown, Footprints, Mountain, Sunrise, Check,
  Volume2, VolumeX, RotateCcw, FastForward, Rewind, Users, Flag,
  Plus, Copy, MapPinned, Dumbbell, ListChecks, Globe, Smartphone,
  MessageCircle, ThumbsUp, Camera, Image, ArrowUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Toaster, toast } from "sonner";
import { setStorageItem, getStorageItem, removeStorageItem } from "@/utils/storage";

// Native Services (Capacitor - only works in native app builds)
import { 
  isNative, 
  BackgroundLocationService, 
  HealthService, 
  PushNotificationService,
  initializeNativeServices 
} from "@/services/nativeServices";
import { StepSyncService } from "@/services/nativeServices";

// Voice Coach - Web Speech API
const speak = (text) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    window.speechSynthesis.speak(utterance);
  }
};

// Confetti celebration for badges
const celebrateBadge = () => {
  const colors = ['#C8102E', '#FFD700', '#009639'];
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: colors
  });
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: colors
    });
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: colors
    });
  }, 250);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token storage keys
const TOKEN_KEY = "stay_kool_running_token";
const USER_KEY = "stay_kool_running_user";

// Logo Component - Original uploaded image enhanced
const Logo = ({ size = 100, className = "" }) => (
  <div 
    className={`block mx-auto overflow-hidden ${className}`}
    style={{ 
      height: size, 
      width: size,
      borderRadius: '24px'
    }}
  >
    <img 
      src="/logo.png" 
      alt="Stay Kool Running" 
      style={{ 
        height: '115%', 
        width: '115%', 
        objectFit: 'cover',
        marginLeft: '-7.5%',
        marginTop: '-7.5%'
      }}
    />
  </div>
);

// Rasta Theme Colors
const COLORS = {
  red: "#C8102E",
  black: "#0D0D0D",
  green: "#009639",
  gold: "#FFD700",
  darkGray: "#1A1A1A",
  mediumGray: "#2D2D2D",
  lightGray: "#9CA3AF"
};

// Activity types for workout categorization
const ACTIVITY_TYPES = [
  { id: "run", name: "Run", icon: "🏃", color: COLORS.green },
  { id: "walk", name: "Walk", icon: "🚶", color: COLORS.gold },
  { id: "hike", name: "Hike", icon: "🥾", color: COLORS.red },
  { id: "bike", name: "Bike Ride", icon: "🚴", color: COLORS.green }
];

// Helper function to get activity icon
const getActivityIcon = (activityType) => {
  const activity = ACTIVITY_TYPES.find(t => t.id === activityType);
  return activity ? activity.icon : "🏃";
};

// Axios interceptor
// Helper to deobfuscate token at startup (synchronous version)
const deobfuscateToken = (str) => {
  if (!str) return '';
  const OBFUSCATION_KEY = 'SK_RUN_2024';
  try {
    const decoded = atob(str);
    return decoded.split('').map((char, i) => 
      String.fromCharCode(char.charCodeAt(0) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length))
    ).join('');
  } catch (e) {
    // If decoding fails, value might be in old plaintext format - return as-is
    return str;
  }
};

let currentToken = deobfuscateToken(localStorage.getItem(TOKEN_KEY));
axios.interceptors.request.use((config) => {
  if (currentToken) {
    config.headers.Authorization = `Bearer ${currentToken}`;
  }
  return config;
});

// Achievements data
const ACHIEVEMENTS = [
  { id: "first_run", name: "First Steps", desc: "Complete your first run", icon: Footprints, requirement: { type: "runs", value: 1 }, color: COLORS.green },
  { id: "five_runs", name: "Getting Started", desc: "Complete 5 runs", icon: Star, requirement: { type: "runs", value: 5 }, color: COLORS.gold },
  { id: "ten_runs", name: "Dedicated", desc: "Complete 10 runs", icon: Flame, requirement: { type: "runs", value: 10 }, color: COLORS.red },
  { id: "first_5k", name: "5K Finisher", desc: "Run 3.1 miles in one workout", icon: Medal, requirement: { type: "single_distance", value: 3.1 }, color: COLORS.green },
  { id: "first_10k", name: "10K Warrior", desc: "Run 6.2 miles in one workout", icon: Trophy, requirement: { type: "single_distance", value: 6.2 }, color: COLORS.gold },
  { id: "half_marathon", name: "Half Marathon", desc: "Run 13.1 miles in one workout", icon: Crown, requirement: { type: "single_distance", value: 13.1 }, color: COLORS.red },
  { id: "total_10", name: "10 Mile Club", desc: "Run 10 total miles", icon: Mountain, requirement: { type: "total_distance", value: 10 }, color: COLORS.green },
  { id: "total_50", name: "50 Mile Club", desc: "Run 50 total miles", icon: Mountain, requirement: { type: "total_distance", value: 50 }, color: COLORS.gold },
  { id: "total_100", name: "Century Runner", desc: "Run 100 total miles", icon: Crown, requirement: { type: "total_distance", value: 100 }, color: COLORS.red },
  { id: "early_bird", name: "Early Bird", desc: "Complete a run before 7 AM", icon: Sunrise, requirement: { type: "early_run", value: 7 }, color: COLORS.gold },
  { id: "speed_demon", name: "Speed Demon", desc: "Run a sub-8 minute mile", icon: Zap, requirement: { type: "pace", value: 8 }, color: COLORS.red },
  { id: "weekly_goal", name: "Goal Crusher", desc: "Hit your weekly goal", icon: Target, requirement: { type: "weekly_goal", value: 1 }, color: COLORS.green },
];

// Haversine formula for distance calculation
const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const formatPace = (pace) => {
  // Show --:-- for invalid or zero pace
  if (!pace || pace <= 0 || !isFinite(pace)) return "--:--";
  // Show actual pace without artificial caps
  const mins = Math.floor(pace);
  const secs = Math.round((pace - mins) * 60);
  // Handle edge case where secs rounds to 60
  if (secs >= 60) {
    return `${mins + 1}:00`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Parse pace string like "9:30" to decimal minutes (9.5)
const parsePaceString = (paceStr) => {
  if (!paceStr || typeof paceStr !== 'string') return null;
  const parts = paceStr.split(':');
  if (parts.length !== 2) return null;
  const mins = parseInt(parts[0], 10);
  const secs = parseInt(parts[1], 10);
  if (isNaN(mins) || isNaN(secs)) return null;
  return mins + (secs / 60);
};

// Format elevation in feet
const formatElevation = (meters) => {
  if (!meters && meters !== 0) return "--";
  const feet = Math.round(meters * 3.28084);
  return `${feet} ft`;
};

const MapFollower = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView([position.lat, position.lng], 16);
  }, [position, map]);
  return null;
};

// Get pace color based on speed (green = fast, yellow = moderate, red = slow)
const getPaceColor = (paceMinPerMile) => {
  if (paceMinPerMile < 8) return "#00FF7F";      // Fast - Green
  if (paceMinPerMile < 10) return "#009639";     // Good - Dark Green
  if (paceMinPerMile < 12) return "#FFD700";     // Moderate - Yellow
  if (paceMinPerMile < 15) return "#FFA500";     // Slow - Orange
  return "#C8102E";                               // Very slow - Red
};

// Heat Map Mini Component for workout history
const HeatMapMini = ({ route }) => {
  if (!route || route.length < 2) return null;
  
  // Calculate segments with pace colors
  const segments = [];
  for (let i = 1; i < route.length; i++) {
    const prev = route[i - 1];
    const curr = route[i];
    
    // Calculate distance for this segment (in miles)
    const R = 3959; // Earth's radius in miles
    const dLat = (curr.lat - prev.lat) * Math.PI / 180;
    const dLon = (curr.lng - prev.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(prev.lat * Math.PI / 180) * Math.cos(curr.lat * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const segmentDist = R * c;
    
    // Calculate time for this segment (in seconds)
    const prevTime = prev.timestamp ? new Date(prev.timestamp).getTime() : 0;
    const currTime = curr.timestamp ? new Date(curr.timestamp).getTime() : 0;
    const segmentTime = (currTime - prevTime) / 1000;
    
    // Calculate pace (min per mile)
    let pace = 10; // Default moderate pace
    if (segmentDist > 0.001 && segmentTime > 0) {
      pace = (segmentTime / 60) / segmentDist;
      pace = Math.max(5, Math.min(20, pace)); // Clamp between 5-20 min/mile
    }
    
    segments.push({
      positions: [[prev.lat, prev.lng], [curr.lat, curr.lng]],
      color: getPaceColor(pace)
    });
  }
  
  const positions = route.map(p => [p.lat, p.lng]);
  
  return (
    <MapContainer bounds={positions} className="w-full h-32 rounded-lg" zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} attributionControl={false}>
      <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
      {segments.map((seg, idx) => (
        <Polyline key={idx} positions={seg.positions} color={seg.color} weight={4} opacity={0.9} />
      ))}
    </MapContainer>
  );
};

const MiniMap = ({ route }) => {
  if (!route || route.length < 2) return null;
  const positions = route.map(p => [p.lat, p.lng]);
  return (
    <MapContainer bounds={positions} className="w-full h-24 rounded-lg" zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} attributionControl={false}>
      <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
      <Polyline positions={positions} color="#00FF7F" weight={4} />
    </MapContainer>
  );
};

// Route Replay Component
const RouteReplayMap = ({ route, replayIndex, isPlaying }) => {
  const map = useMap();
  
  useEffect(() => {
    if (route && route.length > 0 && replayIndex < route.length) {
      const point = route[replayIndex];
      map.setView([point.lat, point.lng], 16, { animate: true, duration: 0.3 });
    }
  }, [replayIndex, route, map]);
  
  if (!route || route.length < 2) return null;
  
  const positions = route.map(p => [p.lat, p.lng]);
  const completedPositions = positions.slice(0, replayIndex + 1);
  const currentPoint = route[replayIndex];
  
  return (
    <>
      <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
      <Polyline positions={positions} color="#888888" weight={3} opacity={0.4} />
      {completedPositions.length > 1 && (
        <Polyline positions={completedPositions} color="#00FF7F" weight={5} />
      )}
      {currentPoint && (
        <CircleMarker 
          center={[currentPoint.lat, currentPoint.lng]} 
          radius={8} 
          fillColor={COLORS.gold}
          color={COLORS.black}
          weight={2}
          fillOpacity={1}
        />
      )}
    </>
  );
};

function App() {
  const [activeTab, setActiveTab] = useState("home");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState("login");
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "" });
  
  const [workouts, setWorkouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [weeklyGoal, setWeeklyGoal] = useState(10);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [earnedAchievements, setEarnedAchievements] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareWorkout, setShareWorkout] = useState(null);
  
  const [isTracking, setIsTracking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [trackingData, setTrackingData] = useState({ route: [], distance: 0, duration: 0, pace: 0, startTime: null });
  const [currentPosition, setCurrentPosition] = useState(null);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  
  // Split times state
  const [splitTimes, setSplitTimes] = useState([]); // Array of {mile: 1, time: "8:45", pace: 8.75}
  const lastSplitMile = useRef(0);
  const lastSplitTime = useRef(0);
  
  // Activity type selection state
  const [showActivityTypeModal, setShowActivityTypeModal] = useState(false);
  const [pendingWorkoutData, setPendingWorkoutData] = useState(null);
  const [selectedActivityType, setSelectedActivityType] = useState("run");
  
  // Step tracker state
  const [dailySteps, setDailySteps] = useState(0);
  const [stepGoal, setStepGoal] = useState(10000);
  const [workoutSteps, setWorkoutSteps] = useState(0);
  const lastStepDistance = useRef(0);
  const STEPS_PER_MILE = 2000; // Average steps per mile for running
  
  // Voice coaching state
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const lastMileAnnounced = useRef(0);
  
  // Weather state
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(null);
  
  // Pace alerts state
  const [targetPace, setTargetPace] = useState(null); // null means no alert, e.g., 9.5 = 9:30/mi
  const [showPaceModal, setShowPaceModal] = useState(false);
  const [tempTargetPace, setTempTargetPace] = useState("9:00");
  const lastPaceAlert = useRef(0); // Prevent alert spam
  
  // Elevation tracking state
  const [elevationGain, setElevationGain] = useState(0);
  const [currentElevation, setCurrentElevation] = useState(null);
  const lastElevation = useRef(null);
  
  // Workout photo state
  const [workoutPhoto, setWorkoutPhoto] = useState(null);
  const photoInputRef = useRef(null);
  
  // Route replay state
  const [showReplayModal, setShowReplayModal] = useState(false);
  const [replayWorkout, setReplayWorkout] = useState(null);
  const [replayIndex, setReplayIndex] = useState(0);
  const [isReplaying, setIsReplaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const replayIntervalRef = useRef(null);
  
  // Challenges state
  const [challenges, setChallenges] = useState([]);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [challengeLeaderboard, setChallengeLeaderboard] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showStepBattleModal, setShowStepBattleModal] = useState(false);
  const [showWeeklyBattleModal, setShowWeeklyBattleModal] = useState(false);
  const [newChallenge, setNewChallenge] = useState({ name: "", description: "", challenge_type: "weekly_miles", start_date: "", end_date: "" });
  const [newStepBattle, setNewStepBattle] = useState({ name: "", opponent_email: "", target_steps: 10000, days: 7 });
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [stepSyncEnabled, setStepSyncEnabled] = useState(false);
  
  // Training Plans state
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [activePlanSegment, setActivePlanSegment] = useState(0);
  const [isPlanRunning, setIsPlanRunning] = useState(false);
  const [planTimer, setPlanTimer] = useState(0);
  const planTimerRef = useRef(null);
  
  // Featured Races state
  const [featuredRaces, setFeaturedRaces] = useState([]);
  const [worldMarathons, setWorldMarathons] = useState([]);
  const [runSignUpRaces, setRunSignUpRaces] = useState([]);
  const [allRaces, setAllRaces] = useState([]);
  const [raceCategories, setRaceCategories] = useState([]);
  const [selectedRaceCategory, setSelectedRaceCategory] = useState(null);
  const [racesViewMode, setRacesViewMode] = useState("all"); // "all", "featured", "world", or "runsignup"
  const [raceSearchState, setRaceSearchState] = useState("");
  const [raceSearchQuery, setRaceSearchQuery] = useState("");
  const [raceSearchDistance, setRaceSearchDistance] = useState("");
  const [raceSearchLoading, setRaceSearchLoading] = useState(false);
  const [racesHasMore, setRacesHasMore] = useState(false);
  const [racesOffset, setRacesOffset] = useState(0);
  const [racesTotal, setRacesTotal] = useState(0);
  const [raceCategory, setRaceCategory] = useState(""); // Category filter: trail, half_marathon, ultra, etc.
  
  // Running streaks and PRs
  const [runningStreak, setRunningStreak] = useState({ current: 0, longest: 0 });
  const [personalRecords, setPersonalRecords] = useState({});
  
  // Workout notes
  const [workoutNotes, setWorkoutNotes] = useState("");
  
  // Email verification and password reset
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(true);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  
  // Password Reset Page state
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  
  // HealthKit state
  const [healthKitConnected, setHealthKitConnected] = useState(false);
  const [lastHealthKitSync, setLastHealthKitSync] = useState(null);
  
  // Native Services state (Capacitor)
  const [nativeServicesReady, setNativeServicesReady] = useState(false);
  const [backgroundTrackingEnabled, setBackgroundTrackingEnabled] = useState(false);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  
  // Social Feed state
  const [activityFeed, setActivityFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [activityComments, setActivityComments] = useState({});  // {activity_id: [comments]}
  const [expandedComments, setExpandedComments] = useState({});  // {activity_id: true/false}
  const [newComment, setNewComment] = useState({});  // {activity_id: "text"}
  
  // Plan Scheduler state
  const [scheduledPlans, setScheduledPlans] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [planToSchedule, setPlanToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  
  // Previous achievements for comparison
  const prevAchievementsRef = useRef([]);
  
  const watchIdRef = useRef(null);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  
  // Wake Lock for background tracking - keeps screen awake
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake Lock acquired - tracking will continue');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake Lock released');
        });
      }
    } catch (err) {
      console.log('Wake Lock not supported or denied:', err);
    }
  };
  
  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };
  
  // Re-acquire wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isTracking && !isPaused) {
        await requestWakeLock();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isTracking, isPaused]);

  // Check achievements with celebration
  const checkAchievements = useCallback((statsData, workoutsData) => {
    if (!statsData) return;
    const earned = [];
    
    ACHIEVEMENTS.forEach(achievement => {
      let isEarned = false;
      const req = achievement.requirement;
      
      switch (req.type) {
        case "runs":
          isEarned = statsData.total_workouts >= req.value;
          break;
        case "total_distance":
          isEarned = statsData.total_miles >= req.value;
          break;
        case "single_distance":
          isEarned = workoutsData?.some(w => w.distance >= req.value);
          break;
        case "pace":
          isEarned = statsData.best_pace > 0 && statsData.best_pace <= req.value;
          break;
        case "weekly_goal":
          isEarned = statsData.this_week_miles >= weeklyGoal;
          break;
        case "early_run":
          isEarned = workoutsData?.some(w => {
            const hour = new Date(w.date).getHours();
            return hour < req.value;
          });
          break;
        default:
          break;
      }
      
      if (isEarned) earned.push(achievement.id);
    });
    
    // Check for newly earned achievements
    const newlyEarned = earned.filter(id => !prevAchievementsRef.current.includes(id));
    if (newlyEarned.length > 0 && prevAchievementsRef.current.length > 0) {
      newlyEarned.forEach(id => {
        const achievement = ACHIEVEMENTS.find(a => a.id === id);
        if (achievement) {
          celebrateBadge();
          toast.success(`🏆 Badge Unlocked: ${achievement.name}!`, {
            description: achievement.desc,
            duration: 5000
          });
          if (voiceEnabled) {
            speak(`Congratulations! You earned the ${achievement.name} badge!`);
          }
        }
      });
    }
    
    prevAchievementsRef.current = earned;
    setEarnedAchievements(earned);
  }, [weeklyGoal, voiceEnabled]);

  useEffect(() => {
    const loadAuth = async () => {
      try {
        const token = await getStorageItem(TOKEN_KEY);
        const savedUser = await getStorageItem(USER_KEY);
        if (token && savedUser) {
          currentToken = token;
          const userData = typeof savedUser === 'string' ? JSON.parse(savedUser) : savedUser;
          setUser(userData);
          if (userData.weekly_goal_miles) setWeeklyGoal(userData.weekly_goal_miles);
          axios.post(`${API}/auth/refresh`).then(res => {
            if (res.data.token) {
              currentToken = res.data.token;
              setStorageItem(TOKEN_KEY, res.data.token);
            }
          }).catch(() => {});
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error loading auth:', e);
        }
      } finally {
        setLoading(false);
      }
    };
    loadAuth();
  }, []);

  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [workoutsRes, statsRes, challengesRes, plansRes, racesRes, worldRes, feedRes, userStreaksRes, userPrsRes] = await Promise.all([
        axios.get(`${API}/workouts?limit=50`),
        axios.get(`${API}/workouts/stats/summary`),
        axios.get(`${API}/challenges`).catch(() => ({ data: [] })),
        axios.get(`${API}/training-plans`).catch(() => ({ data: [] })),
        axios.get(`${API}/featured-races`).catch(() => ({ data: { races: [], categories: [] } })),
        axios.get(`${API}/world-marathons`).catch(() => ({ data: [] })),
        axios.get(`${API}/feed`).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API}/user/streaks`).catch(() => ({ data: { current_streak: 0, longest_streak: 0 } })),
        axios.get(`${API}/user/personal-records`).catch(() => ({ data: {} }))
      ]);
      setWorkouts(workoutsRes.data);
      setStats(statsRes.data);
      setChallenges(challengesRes.data);
      setTrainingPlans(plansRes.data);
      setFeaturedRaces(racesRes.data.races || []);
      setRaceCategories(racesRes.data.categories || []);
      setWorldMarathons(worldRes.data || []);
      setActivityFeed(feedRes.data.activities || []);
      setRunningStreak({ current: userStreaksRes.data.current_streak || 0, longest: userStreaksRes.data.longest_streak || 0 });
      setPersonalRecords(userPrsRes.data || {});
      checkAchievements(statsRes.data, workoutsRes.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching data:', e);
      }
      if (e.response?.status === 401) await handleLogout();
    }
  }, [user, checkAchievements]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  // Initialize native services (Capacitor - only on native platforms)
  useEffect(() => {
    const initNative = async () => {
      if (isNative()) {
        console.log('Initializing native services...');
        const results = await initializeNativeServices();
        console.log('Native services initialized:', results);
        
        setNativeServicesReady(true);
        setHealthKitConnected(results.health);
        
        // Initialize push notifications with handler
        if (results.pushNotifications) {
          const token = await PushNotificationService.initialize(
            (notification) => {
              console.log('Push notification received:', notification);
              toast.info(notification.title || 'New notification');
            },
            (token) => {
              console.log('Push token:', token);
              // TODO: Send token to backend to enable server-side push
            }
          );
          setPushNotificationsEnabled(!!token);
        }
      }
    };
    
    initNative();
    
    return () => {
      // Cleanup on unmount
      if (isNative()) {
        PushNotificationService.cleanup();
      }
    };
  }, []);

  // Weather code to description and icon mapping (WMO codes)
  const getWeatherInfo = (code) => {
    const weatherCodes = {
      0: { desc: "Clear sky", icon: "☀️", running: "Perfect" },
      1: { desc: "Mainly clear", icon: "🌤️", running: "Great" },
      2: { desc: "Partly cloudy", icon: "⛅", running: "Great" },
      3: { desc: "Overcast", icon: "☁️", running: "Good" },
      45: { desc: "Foggy", icon: "🌫️", running: "Caution" },
      48: { desc: "Depositing rime fog", icon: "🌫️", running: "Caution" },
      51: { desc: "Light drizzle", icon: "🌦️", running: "Fair" },
      53: { desc: "Moderate drizzle", icon: "🌧️", running: "Fair" },
      55: { desc: "Dense drizzle", icon: "🌧️", running: "Challenging" },
      61: { desc: "Slight rain", icon: "🌧️", running: "Fair" },
      63: { desc: "Moderate rain", icon: "🌧️", running: "Challenging" },
      65: { desc: "Heavy rain", icon: "🌧️", running: "Not ideal" },
      71: { desc: "Slight snow", icon: "🌨️", running: "Challenging" },
      73: { desc: "Moderate snow", icon: "🌨️", running: "Not ideal" },
      75: { desc: "Heavy snow", icon: "❄️", running: "Not ideal" },
      80: { desc: "Slight rain showers", icon: "🌦️", running: "Fair" },
      81: { desc: "Moderate rain showers", icon: "🌧️", running: "Challenging" },
      82: { desc: "Violent rain showers", icon: "⛈️", running: "Not ideal" },
      95: { desc: "Thunderstorm", icon: "⛈️", running: "Dangerous" },
      96: { desc: "Thunderstorm with hail", icon: "⛈️", running: "Dangerous" },
      99: { desc: "Thunderstorm with heavy hail", icon: "⛈️", running: "Dangerous" }
    };
    return weatherCodes[code] || { desc: "Unknown", icon: "🌡️", running: "Unknown" };
  };

  // Fetch weather using Open-Meteo (free, no API key)
  const fetchWeather = async () => {
    setWeatherLoading(true);
    setWeatherError(null);
    
    try {
      // Get user's location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
      });
      
      const { latitude, longitude } = position.coords;
      
      // Fetch weather from Open-Meteo
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,apparent_temperature&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`
      );
      
      if (!response.ok) throw new Error("Weather fetch failed");
      
      const data = await response.json();
      const current = data.current;
      const weatherInfo = getWeatherInfo(current.weather_code);
      
      setWeather({
        temp: Math.round(current.temperature_2m),
        feelsLike: Math.round(current.apparent_temperature),
        humidity: current.relative_humidity_2m,
        windSpeed: Math.round(current.wind_speed_10m),
        description: weatherInfo.desc,
        icon: weatherInfo.icon,
        runningCondition: weatherInfo.running
      });
    } catch (e) {
      setWeatherError("Could not get weather");
    }
    setWeatherLoading(false);
  };

  // Fetch weather on mount if user is logged in
  useEffect(() => {
    if (user && !weather && !weatherLoading) {
      fetchWeather();
    }
  }, [user]);

  // Fetch RunSignUp races by state
  const fetchRunSignUpRaces = async (state) => {
    setRaceSearchLoading(true);
    try {
      const response = await axios.get(`${API}/discover-races?state=${state}&results_per_page=20`);
      setRunSignUpRaces(response.data.races || []);
    } catch (e) {
      toast.error("Failed to load races");
      setRunSignUpRaces([]);
    }
    setRaceSearchLoading(false);
  };

  // Unified race search with pagination
  const searchAllRaces = async (loadMore = false, categoryOverride = null) => {
    setRaceSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (raceSearchQuery) params.append("query", raceSearchQuery);
      if (raceSearchState) params.append("state", raceSearchState);
      if (raceSearchDistance) params.append("distance", raceSearchDistance);
      
      // Use override if provided, otherwise use state
      const categoryToUse = categoryOverride !== null ? categoryOverride : raceCategory;
      if (categoryToUse) params.append("category", categoryToUse);
      
      params.append("limit", "20");
      
      const currentOffset = loadMore ? racesOffset : 0;
      params.append("offset", currentOffset.toString());
      
      const response = await axios.get(`${API}/races/search?${params.toString()}`);
      const { races, total, has_more, offset: newOffset, limit } = response.data;
      
      if (loadMore) {
        setAllRaces(prev => [...prev, ...races]);
      } else {
        setAllRaces(races || []);
      }
      setRacesTotal(total);
      setRacesHasMore(has_more);
      setRacesOffset(newOffset + limit);
    } catch (e) {
      toast.error("Failed to search races");
      if (!loadMore) setAllRaces([]);
    }
    setRaceSearchLoading(false);
  };
  
  // Load more races
  const loadMoreRaces = () => {
    if (racesHasMore && !raceSearchLoading) {
      searchAllRaces(true);
    }
  };
  
  // Search when category changes
  const handleCategoryChange = (newCategory) => {
    setRaceCategory(newCategory);
    setRacesOffset(0);
    // Pass category directly to avoid stale state
    searchAllRaces(false, newCategory);
  };

  // Load all races on initial view
  useEffect(() => {
    if (user && activeTab === "races" && allRaces.length === 0 && racesViewMode === "all") {
      searchAllRaces();
    }
  }, [activeTab, user, racesViewMode]);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!authForm.email || !authForm.password) { toast.error("Please enter email and password"); return; }
    setAuthLoading(true);
    try {
      const endpoint = authMode === "login" ? "/auth/login" : "/auth/register";
      const payload = authMode === "login" 
        ? { email: authForm.email, password: authForm.password }
        : { email: authForm.email, password: authForm.password, name: authForm.name };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      const { token, user: userData, message } = response.data;
      
      currentToken = token;
      await setStorageItem(TOKEN_KEY, token);
      await setStorageItem(USER_KEY, JSON.stringify(userData));
      
      setUser(userData);
      setEmailVerified(userData.email_verified !== false);
      setShowVerificationBanner(userData.email_verified === false);
      setAuthForm({ email: "", password: "", name: "" });
      
      if (authMode === "register" && message) {
        toast.success(message);
      } else {
        toast.success(authMode === "login" ? "Welcome back!" : "Account created!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setAuthLoading(false);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email");
      return;
    }
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success("If this email exists, a reset link has been sent");
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (e) {
      toast.error("Failed to send reset email");
    }
  };
  
  // Handle password reset from email link
  const handlePasswordReset = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success("Password reset successfully! Please login.");
      setResetToken(null);
      setNewPassword("");
      setConfirmPassword("");
      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };
  
  // Check for reset token in URL on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const resetPath = window.location.pathname.includes('reset-password');
    if (token && resetPath) {
      setResetToken(token);
    }
  }, []);

  // Resend verification email
  const resendVerification = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`);
      toast.success("Verification email sent!");
    } catch (e) {
      toast.error("Failed to send verification email");
    }
  };

  const handleLogout = async () => {
    currentToken = null;
    await removeStorageItem(TOKEN_KEY);
    await removeStorageItem(USER_KEY);
    setUser(null);
    setActiveTab("home");
    toast.success("Logged out");
  };

  const updateWeeklyGoal = async () => {
    const goal = parseFloat(newGoal);
    if (!goal || goal <= 0) { toast.error("Enter a valid goal"); return; }
    try {
      await axios.put(`${API}/users/profile?weekly_goal_miles=${goal}`);
      setWeeklyGoal(goal);
      setShowGoalModal(false);
      setNewGoal("");
      toast.success("Weekly goal updated!");
    } catch (e) {
      toast.error("Failed to update goal");
    }
  };

  const shareWorkoutImage = async (workout, platform = 'native') => {
    const activityEmoji = getActivityIcon(workout.activity_type || 'run');
    const shareText = `${activityEmoji} Stay Kool Running\n📏 ${workout.distance.toFixed(2)} miles\n⏱️ ${formatTime(workout.duration)}\n⚡ ${formatPace(workout.avg_pace)}/mi pace${workout.elevation_gain ? `\n⛰️ ${workout.elevation_gain}ft elevation` : ''}${workout.steps ? `\n👟 ${workout.steps.toLocaleString()} steps` : ''}\n\nstaykoolrunning.co #StayKoolRunning #Running`;
    
    const shareUrl = window.location.origin;
    
    if (platform === 'twitter') {
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      window.open(twitterUrl, '_blank', 'width=550,height=420');
      toast.success("Opening Twitter...");
    } else if (platform === 'instagram') {
      // Instagram doesn't have a direct share URL for text, but we can copy to clipboard
      navigator.clipboard.writeText(shareText);
      toast.success("Caption copied! Open Instagram to share your workout photo");
    } else if (navigator.share) {
      try {
        const shareData = { 
          title: 'Stay Kool Running', 
          text: shareText,
          url: shareUrl
        };
        
        // If workout has a photo, try to share it too
        if (workout.photo && navigator.canShare) {
          try {
            const response = await fetch(workout.photo);
            const blob = await response.blob();
            const file = new File([blob], 'workout.jpg', { type: 'image/jpeg' });
            if (navigator.canShare({ files: [file] })) {
              shareData.files = [file];
            }
          } catch (photoErr) {
            // Photo sharing not supported, continue without
          }
        }
        
        await navigator.share(shareData);
        toast.success("Shared!");
      } catch (e) {
        if (e.name !== 'AbortError') {
          navigator.clipboard.writeText(shareText);
          toast.success("Copied to clipboard!");
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success("Copied to clipboard!");
    }
    setShowShareModal(false);
  };

  // Shared location update handler (used by both native and web geolocation)
  const handleLocationUpdate = (location) => {
    const { lat, lng, accuracy, altitude } = location;
    setGpsAccuracy(accuracy ? Math.round(accuracy) : null);
    setCurrentPosition({ lat, lng });
    
    // Accept all GPS updates - don't filter by accuracy (let user see real data)
    
    // Track elevation if available
    if (altitude !== null && altitude !== undefined) {
      setCurrentElevation(altitude);
      if (lastElevation.current !== null) {
        const elevDiff = altitude - lastElevation.current;
        if (elevDiff > 0) {
          setElevationGain(prev => prev + Math.round(elevDiff * 3.281)); // meters to feet
        }
      }
      lastElevation.current = altitude;
    }
    
    setTrackingData(prev => {
      // Need at least a start time to track
      if (!prev.startTime) return prev;
      
      let newDistance = prev.distance;
      let newRoute = [...prev.route];
      const now = Date.now();
      const duration = Math.floor((now - prev.startTime) / 1000);
      
      // Always add the point to the route for accurate tracking
      const newPoint = { lat, lng, timestamp: new Date().toISOString(), accuracy };
      
      if (prev.route.length > 0) {
        const last = prev.route[prev.route.length - 1];
        
        // Calculate distance using Haversine formula
        const R = 3959; // Earth's radius in miles
        const dLat = (lat - last.lat) * Math.PI / 180;
        const dLon = (lng - last.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(last.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const segmentDistance = R * c;
        
        // Add distance if movement detected (> ~6 feet to filter GPS jitter)
        // Lower threshold to catch more movement
        const MIN_DISTANCE = 0.001; // ~5 feet in miles
        const MAX_DISTANCE = 0.05;  // ~250 feet - ignore GPS jumps
        
        if (segmentDistance >= MIN_DISTANCE && segmentDistance <= MAX_DISTANCE) {
          newDistance += segmentDistance;
          newRoute = [...prev.route, newPoint];
        } else if (segmentDistance > MAX_DISTANCE) {
          // GPS jumped - add point but don't count distance
          newRoute = [...prev.route, newPoint];
        } else {
          // Very small movement - still add point for route accuracy
          newRoute = [...prev.route, newPoint];
        }
      } else {
        // First point - always add it
        newRoute = [newPoint];
      }
      
      // Calculate current pace (minutes per mile) - NO CAPPING
      let newPace = 0;
      if (newDistance > 0 && duration > 0) {
        newPace = (duration / 60) / newDistance;
        // Only filter out impossible values (negative or infinity)
        if (!isFinite(newPace) || newPace < 0) newPace = 0;
      }
      
      // Voice coaching - announce every mile
      const currentMile = Math.floor(newDistance);
      if (currentMile > lastMileAnnounced.current && voiceEnabled) {
        lastMileAnnounced.current = currentMile;
        const paceMinutes = Math.floor(newPace);
        const paceSeconds = Math.round((newPace - paceMinutes) * 60);
        speak(`Mile ${currentMile} complete. Current pace: ${paceMinutes} minutes ${paceSeconds} seconds per mile.`);
        
        // Record split time for this mile
        const splitDuration = duration - lastSplitTime.current;
        const splitPace = splitDuration / 60;
        setSplitTimes(prevSplits => [...prevSplits, {
          mile: currentMile,
          time: formatTime(splitDuration),
          pace: splitPace,
          totalTime: formatTime(duration)
        }]);
        lastSplitTime.current = duration;
      }
      
      // Step tracking - more accurate calculation
      // Average runner: ~1400-1700 steps per mile, walker: ~2000-2400 steps per mile
      const STEPS_PER_MILE_RUNNING = 1600;
      const stepsFromDistance = Math.floor(newDistance * STEPS_PER_MILE_RUNNING);
      const currentWorkoutSteps = stepsFromDistance;
      
      // Update workout steps if increased
      if (currentWorkoutSteps > prev.workoutSteps || 0) {
        const stepIncrease = currentWorkoutSteps - (prev.workoutSteps || 0);
        if (stepIncrease > 0) {
          setWorkoutSteps(currentWorkoutSteps);
          setDailySteps(daily => daily + stepIncrease);
        }
      }
      
      return { 
        ...prev, 
        route: newRoute, 
        distance: newDistance, 
        duration, 
        pace: newPace,
        workoutSteps: currentWorkoutSteps
      };
    });
  };

  // GPS Tracking with Voice Coaching and Step Tracking
  const startTracking = () => {
    if (!navigator.geolocation) { toast.error("GPS not available"); return; }
    setGpsError(null);
    setIsTracking(true);
    setIsPaused(false);
    setTrackingData({ route: [], distance: 0, duration: 0, pace: 0, startTime: Date.now() });
    lastMileAnnounced.current = 0;
    lastStepDistance.current = 0;
    lastPaceAlert.current = 0;
    lastElevation.current = null;
    lastSplitMile.current = 0;
    lastSplitTime.current = 0;
    setSplitTimes([]);
    setWorkoutSteps(0);
    setElevationGain(0);
    setCurrentElevation(null);
    setWorkoutPhoto(null);
    
    if (voiceEnabled) {
      const paceMsg = targetPace ? ` Target pace: ${formatPace(targetPace)} per mile.` : "";
      speak(`Workout started.${paceMsg} Good luck!`);
    }

    timerRef.current = setInterval(() => {
      setTrackingData(prev => {
        if (!prev.startTime) return prev;
        
        const duration = Math.floor((Date.now() - prev.startTime) / 1000);
        
        // Calculate pace - NO CAPPING, show real pace
        let pace = prev.pace;
        if (prev.distance > 0 && duration > 0) {
          pace = (duration / 60) / prev.distance;
          // Only filter impossible values
          if (!isFinite(pace) || pace < 0) pace = 0;
        }
        
        // Pace alert check - warn if pace drops below target (higher number = slower)
        if (targetPace && pace > 0 && voiceEnabled && prev.distance >= 0.1) {
          const now = Date.now();
          // Only alert every 30 seconds minimum and if pace is significantly slower
          if (pace > targetPace + 0.5 && now - lastPaceAlert.current > 30000) {
            lastPaceAlert.current = now;
            speak(`Your pace has dropped to ${formatPace(pace)}. Pick it up to hit your ${formatPace(targetPace)} target!`);
          }
        }
        
        return { ...prev, duration, pace };
      });
    }, 1000);
    
    // Request Wake Lock to keep screen awake and tracking active
    requestWakeLock();

    // Use background location tracking on native platforms
    if (isNative() && BackgroundLocationService.isAvailable()) {
      console.log('Starting background location tracking...');
      setBackgroundTrackingEnabled(true);
      BackgroundLocationService.start(
        (location) => handleLocationUpdate(location),
        (error) => { 
          setGpsError(error.message); 
          toast.error(`GPS Error: ${error.message}`);
          // Fall back to web geolocation
          startWebGeolocation();
        }
      );
    } else {
      // Use standard web geolocation
      startWebGeolocation();
    }
  };
  
  // Web geolocation fallback
  const startWebGeolocation = () => {
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy, altitude } = position.coords;
        // Use the shared handler
        handleLocationUpdate({ lat: latitude, lng: longitude, accuracy, altitude });
      },
      (error) => { setGpsError(error.message); toast.error(`GPS Error: ${error.message}`); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const pauseTracking = () => {
    setIsPaused(true);
    if (timerRef.current) clearInterval(timerRef.current);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    // Release Wake Lock when paused
    releaseWakeLock();
    // Also pause background tracking
    if (isNative() && backgroundTrackingEnabled) {
      BackgroundLocationService.stop();
    }
    if (voiceEnabled) speak("Workout paused.");
  };

  const resumeTracking = async () => {
    setIsPaused(false);
    if (voiceEnabled) speak("Workout resumed.");
    const newStartTime = Date.now() - (trackingData.duration * 1000);
    setTrackingData(prev => ({ ...prev, startTime: newStartTime }));
    
    // Re-acquire Wake Lock
    await requestWakeLock();
    
    timerRef.current = setInterval(() => {
      setTrackingData(prev => {
        if (!prev.startTime) return prev;
        const duration = Math.floor((Date.now() - prev.startTime) / 1000);
        let pace = prev.pace;
        if (prev.distance > 0 && duration > 0) {
          pace = (duration / 60) / prev.distance;
          if (!isFinite(pace) || pace < 0) pace = 0;
        }
        return { ...prev, duration, pace };
      });
    }, 1000);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy, altitude } = position.coords;
        handleLocationUpdate({ lat: latitude, lng: longitude, accuracy, altitude });
      },
      (error) => setGpsError(error.message),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const endTracking = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    
    // Release Wake Lock
    releaseWakeLock();
    
    // Stop background tracking if active
    if (isNative() && backgroundTrackingEnabled) {
      await BackgroundLocationService.stop();
      setBackgroundTrackingEnabled(false);
    }
    
    if (trackingData.distance >= 0.01 && trackingData.duration >= 10) {
      // Store workout data and show activity type selection modal
      setPendingWorkoutData({
        date: new Date().toISOString(),
        duration: trackingData.duration,
        distance: parseFloat(trackingData.distance.toFixed(2)),
        avg_pace: trackingData.pace,
        route: trackingData.route,
        elevation_gain: Math.round(elevationGain * 3.28084), // Convert meters to feet
        steps: workoutSteps,
        split_times: splitTimes
      });
      // CRITICAL: Must set isTracking=false BEFORE showing modal, otherwise tracking UI early return blocks modal
      setIsTracking(false);
      setIsPaused(false);
      setShowActivityTypeModal(true);
    } else {
      toast.info("Workout too short to save");
      setIsTracking(false);
      setIsPaused(false);
      setTrackingData({ route: [], distance: 0, duration: 0, pace: 0, startTime: null });
      setCurrentPosition(null);
      lastMileAnnounced.current = 0;
    }
  };

  // Handle photo selection
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setWorkoutPhoto(event.target.result);
      toast.success("Photo added!");
    };
    reader.onerror = () => {
      toast.error("Failed to load image");
    };
    reader.readAsDataURL(file);
  };

  const saveWorkoutWithType = async (activityType) => {
    if (!pendingWorkoutData) return;
    
    if (voiceEnabled) {
      const typeNames = { run: "run", walk: "walk", hike: "hike", bike: "bike ride" };
      speak(`Great ${typeNames[activityType]}! You covered ${pendingWorkoutData.distance.toFixed(2)} miles in ${formatTime(pendingWorkoutData.duration)}.`);
    }
    
    try {
      const response = await axios.post(`${API}/workouts`, {
        ...pendingWorkoutData,
        activity_type: activityType,
        photo: workoutPhoto,
        notes: workoutNotes || null
      });
      
      // Check for new PR
      if (response.data.new_pr) {
        const prNames = { mile: "Mile", "5k": "5K", "10k": "10K", half_marathon: "Half Marathon", marathon: "Marathon" };
        toast.success(`🏆 New ${prNames[response.data.new_pr.type]} PR!`, { duration: 5000 });
        if (voiceEnabled) {
          speak(`Congratulations! You set a new ${prNames[response.data.new_pr.type]} personal record!`);
        }
      }
      
      toast.success(`${activityType.charAt(0).toUpperCase() + activityType.slice(1)} saved! ${pendingWorkoutData.distance.toFixed(2)} mi`);
      fetchData();
    } catch (e) {
      toast.error("Failed to save workout");
    }
    
    // Reset state
    setShowActivityTypeModal(false);
    setPendingWorkoutData(null);
    setSelectedActivityType("run");
    setIsTracking(false);
    setIsPaused(false);
    setTrackingData({ route: [], distance: 0, duration: 0, pace: 0, startTime: null });
    setCurrentPosition(null);
    setWorkoutPhoto(null);
    setWorkoutNotes("");
    setElevationGain(0);
    lastMileAnnounced.current = 0;
  };

  const discardTracking = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    setIsTracking(false);
    setIsPaused(false);
    setTrackingData({ route: [], distance: 0, duration: 0, pace: 0, startTime: null });
    setCurrentPosition(null);
    setWorkoutPhoto(null);
    setElevationGain(0);
    toast.info("Workout discarded");
  };

  const deleteWorkout = async (workoutId) => {
    if (!window.confirm("Delete this workout?")) return;
    try {
      await axios.delete(`${API}/workouts/${workoutId}`);
      toast.success("Workout deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Route Replay Functions
  const startReplay = (workout) => {
    if (!workout.route || workout.route.length < 2) {
      toast.error("No route data available for this workout");
      return;
    }
    setReplayWorkout(workout);
    setReplayIndex(0);
    setShowReplayModal(true);
    setIsReplaying(false);
  };

  const toggleReplay = () => {
    if (isReplaying) {
      // Pause
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
        replayIntervalRef.current = null;
      }
      setIsReplaying(false);
    } else {
      // Play
      setIsReplaying(true);
      replayIntervalRef.current = setInterval(() => {
        setReplayIndex(prev => {
          if (prev >= replayWorkout.route.length - 1) {
            clearInterval(replayIntervalRef.current);
            replayIntervalRef.current = null;
            setIsReplaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 200 / replaySpeed);
    }
  };

  const resetReplay = () => {
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
    setReplayIndex(0);
    setIsReplaying(false);
  };

  const closeReplay = () => {
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
    setShowReplayModal(false);
    setReplayWorkout(null);
    setReplayIndex(0);
    setIsReplaying(false);
  };

  const changeReplaySpeed = () => {
    const speeds = [1, 2, 4];
    const currentIndex = speeds.indexOf(replaySpeed);
    const newSpeed = speeds[(currentIndex + 1) % speeds.length];
    setReplaySpeed(newSpeed);
    
    // Restart interval with new speed if playing
    if (isReplaying && replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = setInterval(() => {
        setReplayIndex(prev => {
          if (prev >= replayWorkout.route.length - 1) {
            clearInterval(replayIntervalRef.current);
            replayIntervalRef.current = null;
            setIsReplaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 200 / newSpeed);
    }
  };

  // ============ CHALLENGES FUNCTIONS ============
  const createChallenge = async () => {
    if (!newChallenge.name || !newChallenge.start_date || !newChallenge.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const response = await axios.post(`${API}/challenges`, newChallenge);
      setChallenges([response.data, ...challenges]);
      setShowChallengeModal(false);
      setNewChallenge({ name: "", description: "", challenge_type: "weekly_miles", start_date: "", end_date: "" });
      toast.success("Challenge created! Share the code with friends.");
    } catch (e) {
      toast.error("Failed to create challenge");
    }
  };

  const joinChallenge = async () => {
    if (!joinCode) {
      toast.error("Please enter an invite code");
      return;
    }
    try {
      await axios.post(`${API}/challenges/join-by-code?invite_code=${joinCode}`);
      setShowJoinModal(false);
      setJoinCode("");
      fetchData();
      toast.success("Joined challenge!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid invite code");
    }
  };

  const viewChallenge = async (challenge) => {
    try {
      const response = await axios.get(`${API}/challenges/${challenge.id}/leaderboard`);
      setSelectedChallenge(response.data.challenge);
      setChallengeLeaderboard(response.data.leaderboard);
    } catch (e) {
      toast.error("Failed to load challenge details");
    }
  };

  const createStepBattle = async () => {
    if (!newStepBattle.name) {
      toast.error("Please enter a challenge name");
      return;
    }
    try {
      const params = new URLSearchParams({
        name: newStepBattle.name,
        target_steps: newStepBattle.target_steps,
        days: newStepBattle.days
      });
      if (newStepBattle.opponent_email) {
        params.append('opponent_email', newStepBattle.opponent_email);
      }
      const response = await axios.post(`${API}/challenges/step-battle?${params}`);
      setChallenges([response.data, ...challenges]);
      setShowStepBattleModal(false);
      setNewStepBattle({ name: "", opponent_email: "", target_steps: 10000, days: 7 });
      toast.success("Step Battle created! Share the code with your opponent.");
    } catch (e) {
      toast.error("Failed to create step battle");
    }
  };

  const updateChallengeSteps = async (challengeId, steps) => {
    try {
      await axios.post(`${API}/challenges/${challengeId}/update-steps?steps=${steps}`);
      // Refresh challenge leaderboard
      const response = await axios.get(`${API}/challenges/${challengeId}/leaderboard`);
      setChallengeLeaderboard(response.data.leaderboard);
      toast.success("Steps updated!");
    } catch (e) {
      toast.error("Failed to update steps");
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Invite code copied!");
  };


  // ============ STEP SYNC & NOTIFICATIONS ============
  
  // Initialize step sync when app loads
  useEffect(() => {
    const getToken = () => localStorage.getItem(TOKEN_KEY);
    StepSyncService.initialize(API, getToken);
  }, []);
  
  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/user/notifications`);
      setNotifications(response.data);
    } catch (e) {
      console.log('Failed to fetch notifications');
    }
  };
  
  // Fetch notifications periodically
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000); // Every minute
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Toggle step sync (native only)
  const toggleStepSync = async () => {
    if (stepSyncEnabled) {
      StepSyncService.stopAutoSync();
      setStepSyncEnabled(false);
      toast.success("Step auto-sync disabled");
    } else {
      const started = await StepSyncService.startAutoSync(300000); // 5 minutes
      if (started) {
        setStepSyncEnabled(true);
        toast.success("Step auto-sync enabled! Steps will sync every 5 minutes.");
      } else {
        toast.error("Step sync requires the native app with Health permissions");
      }
    }
  };
  
  // Manual sync steps
  const syncStepsNow = async () => {
    try {
      const response = await axios.post(`${API}/user/sync-steps`, { steps: dailySteps });
      toast.success(`Synced ${dailySteps.toLocaleString()} steps to ${response.data.updated_challenges} challenges`);
      if (response.data.notifications_sent > 0) {
        toast.info(`You took the lead! 🏆`);
      }
    } catch (e) {
      toast.error("Failed to sync steps");
    }
  };
  
  // Create weekly step battle
  const createWeeklyBattle = async () => {
    try {
      const params = new URLSearchParams({
        name: newStepBattle.name || "Weekly Step Battle",
        target_steps: newStepBattle.target_steps,
        recurring: true
      });
      const response = await axios.post(`${API}/challenges/weekly-step-battle?${params}`);
      setChallenges([response.data, ...challenges]);
      setShowWeeklyBattleModal(false);
      toast.success("Weekly Step Battle created! Starts Monday.");
    } catch (e) {
      toast.error("Failed to create weekly battle");
    }
  };
  
  // Mark notification as read
  const markNotificationRead = async (notifId) => {
    try {
      await axios.post(`${API}/user/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? {...n, read: true} : n));
    } catch (e) {
      console.log('Failed to mark read');
    }
  };


  // ============ TRAINING PLAN FUNCTIONS ============
  const startTrainingPlan = (plan) => {
    setSelectedPlan(plan);
    setActivePlanSegment(0);
    setPlanTimer(0);
    setIsPlanRunning(false);
    setShowPlanModal(true);
  };

  const togglePlanExecution = () => {
    if (isPlanRunning) {
      // Pause
      if (planTimerRef.current) clearInterval(planTimerRef.current);
      setIsPlanRunning(false);
      if (voiceEnabled) speak("Training paused.");
    } else {
      // Start/Resume
      setIsPlanRunning(true);
      const currentSegment = selectedPlan.segments[activePlanSegment];
      if (voiceEnabled) {
        speak(`Starting ${currentSegment.type}. ${currentSegment.description || ''}`);
      }
      
      planTimerRef.current = setInterval(() => {
        setPlanTimer(prev => {
          const newTime = prev + 1;
          const segment = selectedPlan.segments[activePlanSegment];
          
          // Check if segment is complete
          if (newTime >= segment.duration) {
            // Move to next segment
            const nextSegment = activePlanSegment + 1;
            if (nextSegment < selectedPlan.segments.length) {
              setActivePlanSegment(nextSegment);
              const next = selectedPlan.segments[nextSegment];
              if (voiceEnabled) {
                speak(`${segment.type} complete. Next: ${next.type}. ${next.description || ''}`);
              }
              return 0;
            } else {
              // Plan complete
              clearInterval(planTimerRef.current);
              setIsPlanRunning(false);
              if (voiceEnabled) speak("Training complete! Great workout!");
              toast.success("Training plan completed!");
              return prev;
            }
          }
          
          // Announce halfway and 10 seconds remaining
          if (newTime === Math.floor(segment.duration / 2) && voiceEnabled) {
            speak("Halfway through this segment.");
          }
          if (newTime === segment.duration - 10 && segment.duration > 15 && voiceEnabled) {
            speak("10 seconds remaining.");
          }
          
          return newTime;
        });
      }, 1000);
    }
  };

  const closePlanModal = () => {
    if (planTimerRef.current) clearInterval(planTimerRef.current);
    setShowPlanModal(false);
    setSelectedPlan(null);
    setIsPlanRunning(false);
    setPlanTimer(0);
    setActivePlanSegment(0);
  };

  // ============ HEALTHKIT FUNCTIONS ============
  const connectHealthKit = async () => {
    // This would trigger native HealthKit permission request via Capacitor
    toast.info("HealthKit connection requires the native iOS app. Export with Capacitor to enable.");
    setHealthKitConnected(false);
  };

  const syncHealthKit = async () => {
    toast.info("HealthKit sync requires the native iOS app.");
  };

  // ============ SOCIAL FEED FUNCTIONS ============
  const likeActivity = async (activityId) => {
    try {
      const response = await axios.post(`${API}/feed/like/${activityId}`);
      // Update the activity in the feed
      setActivityFeed(prev => prev.map(activity => {
        if (activity.id === activityId) {
          const likes = activity.likes || [];
          if (response.data.liked) {
            return { ...activity, likes: [...likes, user.id] };
          } else {
            return { ...activity, likes: likes.filter(id => id !== user.id) };
          }
        }
        return activity;
      }));
    } catch (e) {
      toast.error("Failed to like activity");
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Comment functions
  const loadComments = async (activityId) => {
    try {
      const response = await axios.get(`${API}/comments/${activityId}`);
      setActivityComments(prev => ({
        ...prev,
        [activityId]: response.data.comments
      }));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading comments:', e);
      }
    }
  };

  const toggleComments = async (activityId) => {
    const isExpanded = expandedComments[activityId];
    setExpandedComments(prev => ({
      ...prev,
      [activityId]: !isExpanded
    }));
    
    // Load comments if expanding and not already loaded
    if (!isExpanded && !activityComments[activityId]) {
      await loadComments(activityId);
    }
  };

  const submitComment = async (activityId) => {
    const text = newComment[activityId]?.trim();
    if (!text) return;
    
    try {
      const response = await axios.post(`${API}/comments/${activityId}`, { text });
      
      // Add to local state
      setActivityComments(prev => ({
        ...prev,
        [activityId]: [...(prev[activityId] || []), response.data]
      }));
      
      // Update comment count in feed
      setActivityFeed(prev => prev.map(activity => {
        if (activity.id === activityId) {
          return { ...activity, comment_count: (activity.comment_count || 0) + 1 };
        }
        return activity;
      }));
      
      // Clear input
      setNewComment(prev => ({ ...prev, [activityId]: "" }));
    } catch (e) {
      toast.error("Failed to post comment");
    }
  };

  const deleteComment = async (activityId, commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      
      // Remove from local state
      setActivityComments(prev => ({
        ...prev,
        [activityId]: prev[activityId]?.filter(c => c.id !== commentId) || []
      }));
      
      // Update comment count in feed
      setActivityFeed(prev => prev.map(activity => {
        if (activity.id === activityId) {
          return { ...activity, comment_count: Math.max(0, (activity.comment_count || 1) - 1) };
        }
        return activity;
      }));
      
      toast.success("Comment deleted");
    } catch (e) {
      toast.error("Failed to delete comment");
    }
  };

  // ============ PLAN SCHEDULER FUNCTIONS ============
  const fetchCalendarData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scheduled-plans/calendar?month=${calendarMonth}&year=${calendarYear}`);
      setCalendarData(response.data.days || {});
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching calendar:', e);
      }
    }
  }, [calendarMonth, calendarYear]);

  useEffect(() => {
    if (user && activeTab === "training") {
      fetchCalendarData();
    }
  }, [user, activeTab, fetchCalendarData]);

  const openScheduleModal = (plan) => {
    setPlanToSchedule(plan);
    setScheduleDate("");
    setScheduleTime("");
    setShowScheduleModal(true);
  };

  const schedulePlan = async () => {
    if (!scheduleDate || !planToSchedule) {
      toast.error("Please select a date");
      return;
    }
    try {
      await axios.post(`${API}/scheduled-plans`, {
        plan_id: planToSchedule.id,
        scheduled_date: scheduleDate,
        reminder_time: scheduleTime || null
      });
      toast.success("Training scheduled!");
      setShowScheduleModal(false);
      fetchCalendarData();
    } catch (e) {
      toast.error("Failed to schedule plan");
    }
  };

  const completeScheduledPlan = async (planId) => {
    try {
      await axios.put(`${API}/scheduled-plans/${planId}/complete`);
      toast.success("Marked as completed!");
      fetchCalendarData();
    } catch (e) {
      toast.error("Failed to complete plan");
    }
  };

  const deleteScheduledPlan = async (planId) => {
    try {
      await axios.delete(`${API}/scheduled-plans/${planId}`);
      toast.success("Removed from schedule");
      fetchCalendarData();
    } catch (e) {
      toast.error("Failed to remove plan");
    }
  };

  const changeMonth = (delta) => {
    let newMonth = calendarMonth + delta;
    let newYear = calendarYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setCalendarMonth(newMonth);
    setCalendarYear(newYear);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 animate-pulse">
            <Logo size={64} />
          </div>
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#009639]" />
        </div>
      </div>
    );
  }

  // Password Reset Page (from email link)
  if (resetToken) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col">
        <Toaster position="top-center" theme="dark" />
        
        {/* Rasta stripe */}
        <div className="h-1 w-full flex">
          <div className="flex-1 bg-[#C8102E]"></div>
          <div className="flex-1 bg-[#FFD700]"></div>
          <div className="flex-1 bg-[#009639]"></div>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4">
              <Logo size={140} />
            </div>
            <h1 className="text-3xl font-bold text-white">Stay Kool Running</h1>
            <p className="text-[#FFD700] text-sm mt-1">Every Mile Has Meaning</p>
          </div>

          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-white text-xl">Reset Your Password</CardTitle>
              <p className="text-gray-400 text-sm mt-2">Enter your new password below</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400 text-xs">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="At least 6 characters" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[#0D0D0D] border-[#2D2D2D] text-white placeholder:text-gray-600"
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="Confirm your password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-[#0D0D0D] border-[#2D2D2D] text-white placeholder:text-gray-600"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              
              <Button 
                onClick={handlePasswordReset}
                disabled={resetLoading}
                className="w-full bg-[#009639] hover:bg-[#007A2E] text-white font-bold h-12 rounded-xl"
                data-testid="reset-password-submit"
              >
                {resetLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
              
              <button 
                onClick={() => {
                  setResetToken(null);
                  window.history.replaceState({}, document.title, window.location.pathname);
                }}
                className="text-center text-sm text-gray-500 block w-full"
              >
                Back to login
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Auth screen
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col">
        <Toaster position="top-center" theme="dark" />
        
        {/* Rasta stripe */}
        <div className="h-1 w-full flex">
          <div className="flex-1 bg-[#C8102E]"></div>
          <div className="flex-1 bg-[#FFD700]"></div>
          <div className="flex-1 bg-[#009639]"></div>
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4">
              <Logo size={140} />
            </div>
            <h1 className="text-3xl font-bold text-white">Stay Kool Running</h1>
            <p className="text-[#FFD700] text-sm mt-1">Every Mile Has Meaning</p>
          </div>

          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-white text-xl">
                {authMode === "login" ? "Welcome Back" : "Create Account"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAuth} className="space-y-4">
                {authMode === "register" && (
                  <div>
                    <Label className="text-gray-400 text-xs">Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input type="text" placeholder="Your name" value={authForm.name}
                        onChange={(e) => setAuthForm({...authForm, name: e.target.value})}
                        className="pl-10 bg-[#0D0D0D] border-[#2D2D2D] text-white placeholder:text-gray-600" />
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-gray-400 text-xs">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input type="email" placeholder="you@example.com" value={authForm.email}
                      onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                      className="pl-10 bg-[#0D0D0D] border-[#2D2D2D] text-white placeholder:text-gray-600" required />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input type={showPassword ? "text" : "password"} placeholder="••••••••" value={authForm.password}
                      onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                      className="pl-10 pr-10 bg-[#0D0D0D] border-[#2D2D2D] text-white placeholder:text-gray-600" required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={authLoading} data-testid="auth-submit-button"
                  className="w-full bg-[#009639] hover:bg-[#007A2E] text-white font-bold h-12 rounded-xl">
                  {authLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>{authMode === "login" ? <LogIn className="w-5 h-5 mr-2" /> : <User className="w-5 h-5 mr-2" />}
                    {authMode === "login" ? "Sign In" : "Create Account"}</>
                  )}
                </Button>
              </form>
              
              {authMode === "login" && (
                <button 
                  onClick={() => setShowForgotPassword(true)}
                  className="text-center text-sm text-[#009639] mt-3 block w-full"
                >
                  Forgot password?
                </button>
              )}
              
              <p className="text-center text-sm text-gray-500 mt-4">
                {authMode === "login" ? "Don't have an account?" : "Already have an account?"}
                <button onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
                  className="ml-1 text-[#009639] font-medium">
                  {authMode === "login" ? "Sign Up" : "Sign In"}
                </button>
              </p>
            </CardContent>
          </Card>
          
          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForgotPassword(false)}>
              <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" onClick={e => e.stopPropagation()}>
                <CardHeader className="text-center">
                  <CardTitle className="text-white text-xl">Reset Password</CardTitle>
                  <p className="text-gray-400 text-sm mt-2">Enter your email to receive a reset link</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input 
                      type="email" 
                      placeholder="Email address"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="bg-[#0D0D0D] border-[#2D2D2D] text-white pl-10 h-12 rounded-xl"
                    />
                  </div>
                  <Button onClick={handleForgotPassword} className="w-full bg-[#009639] hover:bg-[#007A2E] h-12">
                    Send Reset Link
                  </Button>
                  <button onClick={() => setShowForgotPassword(false)} className="w-full text-gray-500 text-sm">
                    Back to login
                  </button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active Tracking View
  if (isTracking) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col">
        <Toaster position="top-center" theme="dark" />
        
        <div className="flex-1 relative">
          {currentPosition ? (
            <MapContainer center={[currentPosition.lat, currentPosition.lng]} zoom={16} className="w-full h-full" zoomControl={false}>
              <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
              {trackingData.route.length > 1 && (
                <Polyline positions={trackingData.route.map(p => [p.lat, p.lng])} color="#00FF7F" weight={5} opacity={1} />
              )}
              <MapFollower position={currentPosition} />
            </MapContainer>
          ) : (
            <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#009639] mx-auto mb-2" />
                <p className="text-gray-400 text-sm">Acquiring GPS...</p>
              </div>
            </div>
          )}
          
          {/* GPS Status - Top Left */}
          <div className="absolute top-4 left-4 bg-[#0D0D0D]/90 backdrop-blur rounded-lg px-3 py-2 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-[#FFD700]' : 'bg-[#009639] animate-pulse'}`} />
            <span className="text-white text-xs font-medium">{isPaused ? 'PAUSED' : gpsAccuracy ? `GPS ±${gpsAccuracy}m` : 'Acquiring...'}</span>
          </div>
          
          {/* Voice Coach Toggle - Top Right */}
          <button 
            onClick={() => setVoiceEnabled(!voiceEnabled)} 
            className={`absolute top-4 right-4 bg-[#0D0D0D]/90 backdrop-blur rounded-lg p-2 ${voiceEnabled ? 'text-[#009639]' : 'text-gray-500'}`}
            data-testid="voice-toggle"
          >
            {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          
          {/* Stats Overlay on Map - Bottom of map */}
          <div className="absolute bottom-4 left-4 right-4 bg-[#0D0D0D]/90 backdrop-blur rounded-xl p-3">
            <div className="grid grid-cols-5 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-[#00FF7F] tabular-nums">{trackingData.distance.toFixed(2)}</p>
                <p className="text-gray-400 text-[10px]">MILES</p>
              </div>
              <div>
                <p className="text-lg font-bold text-white tabular-nums">{formatTime(trackingData.duration)}</p>
                <p className="text-gray-400 text-[10px]">TIME</p>
              </div>
              <div>
                <p className={`text-lg font-bold tabular-nums ${targetPace && trackingData.pace > targetPace + 0.5 ? 'text-[#C8102E]' : 'text-[#FFD700]'}`}>
                  {formatPace(trackingData.pace)}
                </p>
                <p className="text-gray-400 text-[10px]">PACE{targetPace ? ` (${formatPace(targetPace)})` : ''}</p>
              </div>
              <div>
                <p className="text-lg font-bold text-[#C8102E] tabular-nums">{workoutSteps.toLocaleString()}</p>
                <p className="text-gray-400 text-[10px]">STEPS</p>
              </div>
              <div>
                <p className="text-lg font-bold text-[#009639] tabular-nums flex items-center justify-center gap-0.5">
                  <ArrowUp className="w-3 h-3" />{Math.round(elevationGain * 3.28084)}
                </p>
                <p className="text-gray-400 text-[10px]">ELEV FT</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#0D0D0D] border-t border-[#2D2D2D] p-4 pb-8 safe-area-bottom">
          <div className="grid grid-cols-5 gap-2 mb-6">
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#009639] tabular-nums">{trackingData.distance.toFixed(2)}</p>
              <p className="text-gray-400 text-[10px] mt-1">MILES</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-white tabular-nums">{formatTime(trackingData.duration)}</p>
              <p className="text-gray-400 text-[10px] mt-1">TIME</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl sm:text-3xl font-bold tabular-nums ${targetPace && trackingData.pace > targetPace + 0.5 ? 'text-[#C8102E]' : 'text-[#FFD700]'}`}>
                {formatPace(trackingData.pace)}
              </p>
              <p className="text-gray-400 text-[10px] mt-1">PACE</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#C8102E] tabular-nums">{workoutSteps.toLocaleString()}</p>
              <p className="text-gray-400 text-[10px] mt-1">STEPS</p>
            </div>
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#009639] tabular-nums flex items-center justify-center gap-0.5">
                <ArrowUp className="w-4 h-4" />{Math.round(elevationGain * 3.28084)}
              </p>
              <p className="text-gray-400 text-[10px] mt-1">ELEV</p>
            </div>
          </div>

          {/* Split Times */}
          {splitTimes.length > 0 && (
            <div className="bg-[#1A1A1A] rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Timer className="w-4 h-4 text-[#FFD700]" />
                <span className="text-white text-sm font-medium">Split Times</span>
              </div>
              <div className="max-h-24 overflow-y-auto space-y-1">
                {splitTimes.map((split, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Mile {split.mile}</span>
                    <span className="text-white font-mono">{split.time}</span>
                    <span className={`font-mono ${split.pace < (targetPace || 99) ? 'text-[#009639]' : 'text-[#FFD700]'}`}>
                      {formatPace(split.pace)}/mi
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-6">
            {isPaused ? (
              <button onClick={resumeTracking} className="w-20 h-20 rounded-full bg-[#009639] flex items-center justify-center shadow-lg" data-testid="resume-tracking-button">
                <Play className="w-10 h-10 text-white ml-1" />
              </button>
            ) : (
              <button onClick={pauseTracking} className="w-20 h-20 rounded-full bg-[#FFD700] flex items-center justify-center" data-testid="pause-tracking-button">
                <Pause className="w-10 h-10 text-[#0D0D0D]" />
              </button>
            )}
            <button onClick={endTracking} className="w-20 h-20 rounded-full bg-[#C8102E] flex items-center justify-center" data-testid="end-tracking-button">
              <Square className="w-8 h-8 text-white" />
            </button>
          </div>
          
          <button onClick={discardTracking} className="w-full text-center mt-4 text-gray-500 text-sm">Discard Run</button>
        </div>
      </div>
    );
  }

  const weeklyProgress = stats ? Math.min((stats.this_week_miles / weeklyGoal) * 100, 100) : 0;

  // Main App
  return (
    <div className="min-h-screen bg-[#0D0D0D] pb-20">
      <Toaster position="top-center" theme="dark" />
      
      {/* Home Tab */}
      {activeTab === "home" && (
        <div className="p-4 space-y-5 animate-fade-in">
          {/* Rasta Header */}
          <div className="h-1 w-full flex rounded-full overflow-hidden">
            <div className="flex-1 bg-[#C8102E]"></div>
            <div className="flex-1 bg-[#FFD700]"></div>
            <div className="flex-1 bg-[#009639]"></div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Logo size={48} />
              <div>
                <h1 className="text-white text-lg font-bold">Stay Kool Running</h1>
                <p className="text-gray-400 text-xs">{user.name || user.email.split('@')[0]}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Notification Bell */}
              <button 
                onClick={() => setShowNotifications(true)} 
                className="p-2 text-gray-400 hover:text-white relative"
              >
                <Bell className="w-5 h-5" />
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C8102E] text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="p-2 text-gray-400 hover:text-white">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Weekly Goal Progress */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#2D2D2D] overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-white font-medium">Weekly Goal</span>
                </div>
                <button onClick={() => { setNewGoal(weeklyGoal.toString()); setShowGoalModal(true); }} className="text-[#009639] text-sm">Edit</button>
              </div>
              <div className="flex items-end justify-between mb-2">
                <span className="text-3xl font-bold text-white">{stats?.this_week_miles?.toFixed(1) || 0}</span>
                <span className="text-gray-400">/ {weeklyGoal} mi</span>
              </div>
              <div className="h-3 bg-[#2D2D2D] rounded-full overflow-hidden">
                <div className="h-full rasta-gradient rounded-full transition-all duration-500" style={{ width: `${weeklyProgress}%` }} />
              </div>
              {weeklyProgress >= 100 && (
                <p className="text-[#009639] text-sm mt-2 flex items-center gap-1"><Check className="w-4 h-4" /> Goal achieved! 🎉</p>
              )}
            </CardContent>
          </Card>

          {/* Running Streak Card */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#2D2D2D] overflow-hidden" data-testid="streak-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#C8102E]/20 flex items-center justify-center">
                    <Flame className="w-6 h-6 text-[#C8102E]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Current Streak</p>
                    <p className="text-2xl font-bold text-white">{runningStreak.current} <span className="text-sm font-normal text-gray-400">days</span></p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-xs">Best</p>
                  <p className="text-[#FFD700] font-bold">{runningStreak.longest} days</p>
                </div>
              </div>
              {runningStreak.current >= 7 && (
                <p className="text-[#009639] text-sm mt-2 flex items-center gap-1"><Award className="w-4 h-4" /> Week streak! Keep it up! 🔥</p>
              )}
            </CardContent>
          </Card>

          {/* Weather Card */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#2D2D2D] overflow-hidden" data-testid="weather-card">
            <CardContent className="p-4">
              {weatherLoading ? (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  <span className="text-gray-400 text-sm ml-2">Getting weather...</span>
                </div>
              ) : weatherError ? (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">{weatherError}</span>
                  <button onClick={fetchWeather} className="text-[#009639] text-sm">Retry</button>
                </div>
              ) : weather ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{weather.icon}</span>
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-white">{weather.temp}°F</span>
                        <span className="text-gray-500 text-sm">Feels {weather.feelsLike}°</span>
                      </div>
                      <p className="text-gray-400 text-sm">{weather.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span>💨 {weather.windSpeed} mph</span>
                      <span>💧 {weather.humidity}%</span>
                    </div>
                    <p className={`text-sm font-medium mt-1 ${
                      weather.runningCondition === 'Perfect' || weather.runningCondition === 'Great' ? 'text-[#009639]' :
                      weather.runningCondition === 'Good' || weather.runningCondition === 'Fair' ? 'text-[#FFD700]' :
                      weather.runningCondition === 'Challenging' ? 'text-orange-500' :
                      'text-[#C8102E]'
                    }`}>
                      {weather.runningCondition} for running
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">Weather unavailable</span>
                  <button onClick={fetchWeather} className="text-[#009639] text-sm flex items-center gap-1">
                    <Navigation className="w-4 h-4" /> Get Weather
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Start Run Button with Pace Target */}
          <div className="space-y-2">
            <button onClick={startTracking} data-testid="start-run-button"
              className="w-full py-6 rounded-2xl bg-[#009639] flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(0,150,57,0.4)] active:scale-[0.98] transition-transform">
              <Play className="w-8 h-8 text-white" />
              <span className="text-white text-xl font-bold">Start Run</span>
            </button>
            <div 
              onClick={() => !targetPace && setShowPaceModal(true)}
              className="w-full py-2 rounded-xl bg-[#1A1A1A] border border-[#2D2D2D] flex items-center justify-center gap-2 text-gray-400 hover:text-white hover:border-[#FFD700] transition-colors cursor-pointer"
              data-testid="pace-target-button"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && !targetPace && setShowPaceModal(true)}
            >
              <Target className="w-4 h-4" />
              <span className="text-sm">
                {targetPace ? `Target Pace: ${formatPace(targetPace)}/mi` : 'Set Target Pace'}
              </span>
              {targetPace && (
                <span 
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setTargetPace(null); toast.info("Pace target cleared"); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setTargetPace(null); toast.info("Pace target cleared"); }}}
                  className="ml-2 text-gray-500 hover:text-[#C8102E] cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </span>
              )}
            </div>
          </div>

          {/* Daily Steps Card - Large with Circular Progress */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#2D2D2D] overflow-hidden" data-testid="daily-steps-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Footprints className="w-6 h-6 text-[#C8102E]" />
                  <span className="text-white font-semibold text-lg">Daily Steps</span>
                </div>
                <button 
                  onClick={() => {
                    const newGoal = prompt("Set your daily step goal:", stepGoal);
                    if (newGoal && !isNaN(parseInt(newGoal))) {
                      setStepGoal(parseInt(newGoal));
                      toast.success(`Step goal set to ${parseInt(newGoal).toLocaleString()}`);
                    }
                  }}
                  className="text-gray-400 text-sm hover:text-[#009639] transition-colors"
                >
                  Goal: {stepGoal.toLocaleString()}
                </button>
              </div>
              
              {/* Circular Progress Meter */}
              <div className="flex items-center justify-center mb-4">
                <div className="relative w-48 h-48">
                  {/* Background circle */}
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#2D2D2D"
                      strokeWidth="12"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="url(#stepGradient)"
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 88}
                      strokeDashoffset={2 * Math.PI * 88 * (1 - Math.min(dailySteps / stepGoal, 1))}
                      className="transition-all duration-500"
                    />
                    <defs>
                      <linearGradient id="stepGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#C8102E" />
                        <stop offset="50%" stopColor="#FFD700" />
                        <stop offset="100%" stopColor="#009639" />
                      </linearGradient>
                    </defs>
                  </svg>
                  {/* Center content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-5xl font-bold text-white tabular-nums">{dailySteps.toLocaleString()}</span>
                    <span className="text-gray-400 text-sm mt-1">of {stepGoal.toLocaleString()}</span>
                    <span className="text-[#FFD700] text-lg font-semibold mt-1">
                      {Math.round((dailySteps / stepGoal) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Linear progress bar below */}
              <div className="h-2 bg-[#2D2D2D] rounded-full overflow-hidden mb-3">
                <div 
                  className="h-full bg-gradient-to-r from-[#C8102E] via-[#FFD700] to-[#009639] rounded-full transition-all duration-500" 
                  style={{ width: `${Math.min((dailySteps / stepGoal) * 100, 100)}%` }} 
                />
              </div>
              
              {/* Steps remaining or goal achieved */}
              {dailySteps >= stepGoal ? (
                <p className="text-[#009639] text-center font-semibold flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" /> Step goal achieved! 🎉
                </p>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  {(stepGoal - dailySteps).toLocaleString()} steps to go
                </p>
              )}
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#009639]" />
                  <span className="text-gray-400 text-xs">Total Miles</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats?.total_miles || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-[#FFD700]" />
                  <span className="text-gray-400 text-xs">Best Pace</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatPace(stats?.best_pace)} /mi</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mountain className="w-4 h-4 text-[#C8102E]" />
                  <span className="text-gray-400 text-xs">Longest Run</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats?.longest_run || 0} mi</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Logo size={16} />
                  <span className="text-gray-400 text-xs">Total Runs</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats?.total_workouts || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Workouts */}
          {workouts.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-bold">Recent Runs</h2>
                <button onClick={() => setActiveTab("history")} className="text-[#009639] text-sm flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                {workouts.slice(0, 2).map(w => (
                  <Card key={w.id} className="bg-[#1A1A1A] border-[#2D2D2D]">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#009639]/20 flex items-center justify-center text-2xl">
                        {getActivityIcon(w.activity_type)}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{w.distance.toFixed(2)} mi</p>
                        <p className="text-gray-400 text-xs">
                          {new Date(w.date).toLocaleDateString()} • {formatTime(w.duration)} • {formatPace(w.avg_pace)}/mi
                          {w.elevation_gain > 0 && ` • ↑${w.elevation_gain}ft`}
                        </p>
                      </div>
                      <button onClick={() => { setShareWorkout(w); setShowShareModal(true); }} className="p-2 text-gray-500 hover:text-[#009639]">
                        <Share2 className="w-5 h-5" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="p-4 space-y-4 animate-fade-in">
          <h1 className="text-white text-xl font-bold pt-2">Workout History</h1>
          
          {workouts.length === 0 ? (
            <div className="text-center py-20">
              <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No workouts yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {workouts.map(w => (
                <Card key={w.id} className="bg-[#1A1A1A] border-[#2D2D2D] overflow-hidden">
                  {w.photo && (
                    <div className="relative h-40 overflow-hidden">
                      <img src={w.photo} alt="Workout" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
                    </div>
                  )}
                  {!w.photo && w.route && w.route.length > 1 && <HeatMapMini route={w.route} />}
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getActivityIcon(w.activity_type)}</span>
                        <p className="text-white font-bold text-lg">{w.distance.toFixed(2)} miles</p>
                      </div>
                      <div className="flex gap-2">
                        {w.route && w.route.length > 1 && (
                          <button onClick={() => startReplay(w)} className="text-gray-500 hover:text-[#FFD700]" data-testid="replay-route-button">
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => { setShareWorkout(w); setShowShareModal(true); }} className="text-gray-500 hover:text-[#009639]">
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteWorkout(w.id)} className="text-gray-500 hover:text-[#C8102E]">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm flex-wrap">
                      <span className="text-gray-400 flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(w.date).toLocaleDateString()}</span>
                      <span className="text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(w.duration)}</span>
                      <span className="text-[#009639] flex items-center gap-1"><Zap className="w-3 h-3" />{formatPace(w.avg_pace)}/mi</span>
                      {w.elevation_gain > 0 && (
                        <span className="text-[#FFD700] flex items-center gap-1"><ArrowUp className="w-3 h-3" />{w.elevation_gain}ft</span>
                      )}
                      {w.steps > 0 && (
                        <span className="text-[#C8102E] flex items-center gap-1"><Footprints className="w-3 h-3" />{w.steps.toLocaleString()}</span>
                      )}
                    </div>
                    {w.notes && (
                      <p className="text-gray-400 text-sm mt-2 italic">&ldquo;{w.notes}&rdquo;</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Achievements Tab */}
      {activeTab === "achievements" && (
        <div className="p-4 space-y-4 animate-fade-in">
          <h1 className="text-white text-xl font-bold pt-2">Achievements</h1>
          <p className="text-gray-400 text-sm">{earnedAchievements.length} of {ACHIEVEMENTS.length} unlocked</p>
          
          <div className="grid grid-cols-2 gap-3">
            {ACHIEVEMENTS.map(achievement => {
              const isEarned = earnedAchievements.includes(achievement.id);
              const Icon = achievement.icon;
              return (
                <Card key={achievement.id} className={`border-[#2D2D2D] ${isEarned ? 'bg-[#1A1A1A]' : 'bg-[#0D0D0D] opacity-50'}`}>
                  <CardContent className="p-4 text-center">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${isEarned ? '' : 'bg-[#2D2D2D]'}`}
                      style={isEarned ? { backgroundColor: achievement.color + '30' } : {}}>
                      <Icon className="w-6 h-6" style={{ color: isEarned ? achievement.color : '#4B5563' }} />
                    </div>
                    <p className="text-white font-medium text-sm">{achievement.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{achievement.desc}</p>
                    {isEarned && <p className="text-[#009639] text-xs mt-2 flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Unlocked</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === "profile" && (
        <div className="p-4 space-y-6 animate-fade-in">
          <h1 className="text-white text-xl font-bold pt-2">Profile</h1>
          
          <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
            <CardContent className="p-6 text-center">
              <div className="w-20 h-20 rounded-full rasta-gradient flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-white text-xl font-bold">{user.name || 'Runner'}</h2>
              <p className="text-gray-400 text-sm">{user.email}</p>
              {/* Streak badges */}
              <div className="flex justify-center gap-4 mt-4">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#C8102E]/20 flex items-center justify-center mx-auto">
                    <Flame className="w-6 h-6 text-[#C8102E]" />
                  </div>
                  <p className="text-white font-bold mt-1">{runningStreak.current}</p>
                  <p className="text-gray-500 text-xs">Day Streak</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center mx-auto">
                    <Trophy className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <p className="text-white font-bold mt-1">{runningStreak.longest}</p>
                  <p className="text-gray-500 text-xs">Best Streak</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Records */}
          <Card className="bg-[#1A1A1A] border-[#2D2D2D]" data-testid="personal-records-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Medal className="w-5 h-5 text-[#FFD700]" /> Personal Records
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {[
                  { key: "mile", name: "Mile", icon: "🏃" },
                  { key: "5k", name: "5K (3.1 mi)", icon: "🎯" },
                  { key: "10k", name: "10K (6.2 mi)", icon: "💪" },
                  { key: "half_marathon", name: "Half Marathon", icon: "🏅" },
                  { key: "marathon", name: "Marathon", icon: "🏆" }
                ].map(pr => (
                  <div key={pr.key} className="flex items-center justify-between py-2 border-b border-[#2D2D2D] last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{pr.icon}</span>
                      <span className="text-white">{pr.name}</span>
                    </div>
                    <span className={`font-mono font-bold ${personalRecords[pr.key]?.formatted ? 'text-[#009639]' : 'text-gray-500'}`}>
                      {personalRecords[pr.key]?.formatted || "--:--"}
                    </span>
                  </div>
                ))}
              </div>
              {!Object.values(personalRecords).some(pr => pr?.formatted) && (
                <p className="text-gray-500 text-sm text-center mt-4">Complete workouts to set personal records!</p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
            <CardContent className="p-4">
              <h3 className="text-white font-bold mb-4">All-Time Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Miles</span>
                  <span className="text-[#009639] font-bold">{stats?.total_miles || 0} mi</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Workouts</span>
                  <span className="text-white font-bold">{stats?.total_workouts || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Avg Pace</span>
                  <span className="text-white font-bold">{formatPace(stats?.avg_pace)} /mi</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Achievements</span>
                  <span className="text-[#FFD700] font-bold">{earnedAchievements.length}/{ACHIEVEMENTS.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* HealthKit Integration */}
          <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-[#009639]" />
                  <h3 className="text-white font-bold">Apple Health</h3>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${healthKitConnected ? 'bg-[#009639]/20 text-[#009639]' : 'bg-gray-600/20 text-gray-400'}`}>
                  {healthKitConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-4">Sync your runs with Apple Health for a complete fitness picture.</p>
              {healthKitConnected ? (
                <div className="space-y-2">
                  <Button onClick={syncHealthKit} className="w-full bg-[#009639] hover:bg-[#007A2E]">
                    Sync Now
                  </Button>
                  {lastHealthKitSync && (
                    <p className="text-gray-500 text-xs text-center">Last synced: {new Date(lastHealthKitSync).toLocaleString()}</p>
                  )}
                </div>
              ) : (
                <Button onClick={connectHealthKit} variant="outline" className="w-full border-[#009639] text-[#009639]">
                  Connect Apple Health
                </Button>
              )}
            </CardContent>
          </Card>

          <Button onClick={handleLogout} variant="outline" className="w-full border-[#C8102E]/50 text-[#C8102E] hover:bg-[#C8102E]/10">
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      )}

      {/* Challenges Tab */}
      {activeTab === "challenges" && (
        <div className="p-4 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between pt-2">
            <h1 className="text-white text-xl font-bold">Challenges</h1>
            <div className="flex gap-2">
              <Button onClick={() => setShowJoinModal(true)} size="sm" variant="outline" className="border-[#009639] text-[#009639]">
                Join
              </Button>
              <Button onClick={() => setShowChallengeModal(true)} size="sm" className="bg-[#009639]">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Step Battle Quick Create */}
          <Card className="bg-gradient-to-r from-[#C8102E]/20 to-[#009639]/20 border-[#FFD700]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                    <Footprints className="w-6 h-6 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Step Battle</h3>
                    <p className="text-gray-400 text-xs">Challenge someone head-to-head!</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowStepBattleModal(true)} size="sm" className="flex-1 bg-[#FFD700] text-black hover:bg-[#FFD700]/80">
                  <Zap className="w-4 h-4 mr-1" /> 1v1 Battle
                </Button>
                <Button onClick={() => setShowWeeklyBattleModal(true)} size="sm" className="flex-1 bg-[#009639] text-white hover:bg-[#009639]/80">
                  <Calendar className="w-4 h-4 mr-1" /> Weekly
                </Button>
              </div>
              {/* Sync Steps Button */}
              <div className="mt-3 pt-3 border-t border-[#2D2D2D] flex items-center justify-between">
                <span className="text-gray-500 text-xs">Your steps: {dailySteps.toLocaleString()}</span>
                <Button onClick={syncStepsNow} size="sm" variant="ghost" className="text-[#009639] text-xs h-7">
                  <RotateCcw className="w-3 h-3 mr-1" /> Sync to Battles
                </Button>
              </div>
            </CardContent>
          </Card>

          {challenges.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">No challenges yet</p>
              <p className="text-gray-500 text-sm">Create a challenge and invite friends to compete!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {challenges.map(challenge => (
                <Card key={challenge.id} className={`border-[#2D2D2D] cursor-pointer hover:border-[#009639]/50 transition-colors ${challenge.challenge_type === 'step_battle' ? 'bg-gradient-to-r from-[#1A1A1A] to-[#1A1A1A] border-l-4 border-l-[#FFD700]' : 'bg-[#1A1A1A]'}`} onClick={() => viewChallenge(challenge)}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {challenge.challenge_type === 'step_battle' && <Footprints className="w-4 h-4 text-[#FFD700]" />}
                        <h3 className="text-white font-bold">{challenge.name}</h3>
                      </div>
                      <span className="text-[#FFD700] text-xs">
                        {challenge.challenge_type === 'step_battle' ? 
                          `${(challenge.participant_ids?.length || 0) + 1} vs` : 
                          `${(challenge.participant_ids?.length || 0) + 1} runners`
                        }
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">{challenge.description || `${challenge.challenge_type.replace('_', ' ')} challenge`}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">
                        {new Date(challenge.start_date).toLocaleDateString()} - {new Date(challenge.end_date).toLocaleDateString()}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); copyInviteCode(challenge.invite_code); }} className="text-[#009639] flex items-center gap-1">
                        <Copy className="w-3 h-3" /> {challenge.invite_code}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Training Plans Tab */}
      {activeTab === "training" && (
        <div className="p-4 space-y-4 animate-fade-in">
          <h1 className="text-white text-xl font-bold pt-2">Training Plans</h1>
          <p className="text-gray-400 text-sm">Structured workouts with voice coaching</p>

          {/* Calendar View */}
          <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => changeMonth(-1)} className="p-1 text-gray-400 hover:text-white">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-white font-bold">
                  {new Date(calendarYear, calendarMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h3>
                <button onClick={() => changeMonth(1)} className="p-1 text-gray-400 hover:text-white">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                  <div key={`day-${day}-${idx}`} className="text-gray-500 py-1">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before the 1st */}
                {Array(getFirstDayOfMonth(calendarMonth, calendarYear)).fill(null).map((_, i) => (
                  <div key={`empty-${i}`} className="h-10" />
                ))}
                {/* Days of the month */}
                {Array(getDaysInMonth(calendarMonth, calendarYear)).fill(null).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayData = calendarData[dateStr];
                  const hasPlans = dayData?.plans?.length > 0;
                  const hasWorkouts = dayData?.workouts?.length > 0;
                  const isToday = new Date().toISOString().slice(0, 10) === dateStr;
                  
                  return (
                    <div 
                      key={day} 
                      className={`h-10 rounded flex flex-col items-center justify-center text-xs relative
                        ${isToday ? 'bg-[#009639]/20 border border-[#009639]' : 'bg-[#0D0D0D]'}
                        ${hasPlans || hasWorkouts ? 'cursor-pointer hover:bg-[#2D2D2D]' : ''}
                      `}
                    >
                      <span className={isToday ? 'text-[#009639] font-bold' : 'text-gray-400'}>{day}</span>
                      <div className="flex gap-0.5 mt-0.5">
                        {hasPlans && <div className="w-1.5 h-1.5 rounded-full bg-[#FFD700]" />}
                        {hasWorkouts && <div className="w-1.5 h-1.5 rounded-full bg-[#009639]" />}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#FFD700]" /> Scheduled</span>
                <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#009639]" /> Completed</span>
              </div>
            </CardContent>
          </Card>

          <h2 className="text-white font-bold">Available Plans</h2>
          <div className="space-y-4">
            {trainingPlans.map(plan => (
              <Card key={plan.id} className="bg-[#1A1A1A] border-[#2D2D2D]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-bold">{plan.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${
                      plan.difficulty === 'beginner' ? 'bg-[#009639]/20 text-[#009639]' :
                      plan.difficulty === 'intermediate' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                      'bg-[#C8102E]/20 text-[#C8102E]'
                    }`}>
                      {plan.difficulty}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3">{plan.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{Math.floor(plan.total_duration / 60)} min</span>
                      <span className="flex items-center gap-1"><ListChecks className="w-3 h-3" />{plan.segments?.length || 0} segments</span>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => openScheduleModal(plan)} size="sm" variant="outline" className="border-[#FFD700] text-[#FFD700]">
                        <Calendar className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => startTrainingPlan(plan)} size="sm" className="bg-[#009639]">
                        <Play className="w-4 h-4 mr-1" /> Start
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Races Tab */}
      {activeTab === "races" && (
        <div className="p-4 space-y-4 animate-fade-in">
          <h1 className="text-white text-xl font-bold pt-2">Find Races</h1>
          
          {/* Search Filters */}
          <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
            <CardContent className="p-4 space-y-3">
              {/* Search Input */}
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search by city, name, or event..."
                  value={raceSearchQuery}
                  onChange={(e) => setRaceSearchQuery(e.target.value)}
                  className="bg-[#0D0D0D] border-[#2D2D2D] text-white pl-10 h-10 rounded-lg text-sm"
                  data-testid="race-search-input"
                />
              </div>
              
              {/* Filters Row */}
              <div className="flex gap-2">
                <select
                  value={raceSearchState}
                  onChange={(e) => setRaceSearchState(e.target.value)}
                  className="flex-1 bg-[#0D0D0D] border border-[#2D2D2D] rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="race-state-filter"
                >
                  <option value="">All States</option>
                  <option value="GA">Georgia</option>
                  <option value="FL">Florida</option>
                  <option value="TX">Texas</option>
                  <option value="CA">California</option>
                  <option value="NY">New York</option>
                  <option value="IL">Illinois</option>
                  <option value="NC">North Carolina</option>
                  <option value="VA">Virginia</option>
                  <option value="MD">Maryland</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="OH">Ohio</option>
                  <option value="NJ">New Jersey</option>
                  <option value="MA">Massachusetts</option>
                  <option value="TN">Tennessee</option>
                  <option value="CO">Colorado</option>
                  <option value="DC">Washington DC</option>
                </select>
                
                <select
                  value={raceSearchDistance}
                  onChange={(e) => setRaceSearchDistance(e.target.value)}
                  className="flex-1 bg-[#0D0D0D] border border-[#2D2D2D] rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="race-distance-filter"
                >
                  <option value="">All Distances</option>
                  <option value="5k">5K</option>
                  <option value="10k">10K</option>
                  <option value="half">Half Marathon</option>
                  <option value="marathon">Marathon</option>
                </select>
              </div>
              
              <Button 
                onClick={searchAllRaces}
                disabled={raceSearchLoading}
                className="w-full bg-[#009639] hover:bg-[#007A2E]"
                data-testid="race-search-button"
              >
                {raceSearchLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                Search Races
              </Button>
            </CardContent>
          </Card>

          {/* Source Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
            <button
              onClick={() => { setRacesViewMode("all"); setRaceCategory(""); searchAllRaces(); }}
              className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap bg-[#009639] text-white"
            >
              🏃 All Events
            </button>
          </div>

          {/* All Races View (Unified Search Results) */}
          {racesViewMode === "all" && (
            <>
              {raceSearchLoading ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#009639]" />
                  <p className="text-gray-400 mt-2">Searching races...</p>
                </div>
              ) : allRaces.length === 0 ? (
                <div className="text-center py-10">
                  <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400">No races found. Try different filters.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-400 text-sm">{allRaces.length} of {racesTotal} races found</p>
                  {allRaces.map(race => (
                    <Card key={race.id} className={`bg-[#1A1A1A] border-[#2D2D2D] ${race.is_major ? 'border-l-4 border-l-[#FFD700]' : race.source === 'rbg' ? 'border-l-4 border-l-[#C8102E]' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-white font-bold">{race.name}</h3>
                              {race.source === 'rbg' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C8102E]/20 text-[#C8102E]">RBG</span>}
                              {race.source === 'world' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFD700]/20 text-[#FFD700]">Major</span>}
                            </div>
                            <p className="text-gray-500 text-sm">{race.location}</p>
                          </div>
                          <span className="text-[#009639] text-xs font-bold">{race.distance}</span>
                        </div>
                        {race.description && <p className="text-gray-400 text-sm mb-3 line-clamp-2">{race.description}</p>}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {race.date ? (race.date.includes('T') ? new Date(race.date).toLocaleDateString() : race.date) : "TBD"}
                          </p>
                          {race.url && (
                            <a href={race.url} target="_blank" rel="noopener noreferrer" className="text-[#009639] text-sm flex items-center gap-1">
                              Register <ChevronRight className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Load More Button */}
                  {racesHasMore && (
                    <Button
                      onClick={loadMoreRaces}
                      disabled={raceSearchLoading}
                      className="w-full bg-[#2D2D2D] hover:bg-[#3D3D3D] text-white"
                      data-testid="load-more-races"
                    >
                      {raceSearchLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ChevronRight className="w-4 h-4 mr-2 rotate-90" />
                      )}
                      Load More Races
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Featured Races View (RBG) - Hidden, all in All Events */}
          {racesViewMode === "featured" && null}

          {/* World Marathons View - Hidden, all in All Events */}
          {racesViewMode === "world" && null}

        </div>
      )}

      {/* Social Feed Tab */}
      {activeTab === "feed" && (
        <div className="p-4 space-y-4 animate-fade-in">
          <h1 className="text-white text-xl font-bold pt-2">Activity Feed</h1>
          <p className="text-gray-400 text-sm">See what your challenge friends are up to</p>

          {activityFeed.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">No activities yet</p>
              <p className="text-gray-500 text-sm">Join a challenge to see friends&apos; activities</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activityFeed.map(activity => (
                <Card key={activity.id} className="bg-[#1A1A1A] border-[#2D2D2D]">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C8102E] via-[#FFD700] to-[#009639] flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-bold">{activity.user_name}</span>
                          <span className="text-gray-500 text-xs">{getTimeAgo(activity.created_at)}</span>
                        </div>
                        <p className="text-[#009639] font-medium">{activity.title}</p>
                        <p className="text-gray-400 text-sm">{activity.description}</p>
                        
                        {activity.activity_type === "workout_completed" && activity.data && (
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Zap className="w-3 h-3 text-[#FFD700]" />
                              {activity.data.distance?.toFixed(2)} mi
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {Math.floor((activity.data.duration || 0) / 60)}:{((activity.data.duration || 0) % 60).toString().padStart(2, '0')}
                            </span>
                          </div>
                        )}
                        
                        {/* Like and Comment buttons */}
                        <div className="flex items-center gap-4 mt-3">
                          <button 
                            onClick={() => likeActivity(activity.id)}
                            className={`flex items-center gap-1 text-sm ${
                              activity.likes?.includes(user?.id) ? 'text-[#C8102E]' : 'text-gray-500'
                            }`}
                            data-testid="like-button"
                          >
                            <Heart className={`w-4 h-4 ${activity.likes?.includes(user?.id) ? 'fill-[#C8102E]' : ''}`} />
                            {activity.likes?.length || 0}
                          </button>
                          <button 
                            onClick={() => toggleComments(activity.id)}
                            className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#009639]"
                            data-testid="comment-toggle"
                          >
                            <MessageCircle className="w-4 h-4" />
                            {activity.comment_count || 0}
                          </button>
                        </div>
                        
                        {/* Comments Section */}
                        {expandedComments[activity.id] && (
                          <div className="mt-4 pt-3 border-t border-[#2D2D2D]">
                            {/* Comment Input */}
                            <div className="flex gap-2 mb-3">
                              <Input
                                value={newComment[activity.id] || ""}
                                onChange={e => setNewComment(prev => ({ ...prev, [activity.id]: e.target.value }))}
                                placeholder="Write a comment..."
                                className="flex-1 bg-[#0D0D0D] border-[#2D2D2D] text-white text-sm h-9"
                                onKeyPress={e => e.key === 'Enter' && submitComment(activity.id)}
                                data-testid="comment-input"
                              />
                              <Button 
                                onClick={() => submitComment(activity.id)}
                                size="sm"
                                className="bg-[#009639] hover:bg-[#007A2E] h-9 px-3"
                                disabled={!newComment[activity.id]?.trim()}
                                data-testid="submit-comment"
                              >
                                Post
                              </Button>
                            </div>
                            
                            {/* Comments List */}
                            <div className="space-y-3">
                              {activityComments[activity.id]?.length === 0 && (
                                <p className="text-gray-500 text-sm text-center py-2">No comments yet. Be the first!</p>
                              )}
                              {activityComments[activity.id]?.map(comment => (
                                <div key={comment.id} className="flex gap-2">
                                  <div className="w-6 h-6 rounded-full bg-[#2D2D2D] flex items-center justify-center flex-shrink-0">
                                    <User className="w-3 h-3 text-gray-400" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-white text-sm font-medium">{comment.user_name}</span>
                                      <span className="text-gray-600 text-xs">{getTimeAgo(comment.created_at)}</span>
                                      {comment.user_id === user?.id && (
                                        <button 
                                          onClick={() => deleteComment(activity.id, comment.id)}
                                          className="text-gray-600 hover:text-[#C8102E] ml-auto"
                                          data-testid="delete-comment"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                    <p className="text-gray-300 text-sm">{comment.text}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom Navigation - Updated with Feed tab */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#0D0D0D] border-t border-[#2D2D2D] px-2 py-2 safe-area-bottom">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          <button onClick={() => setActiveTab("home")} data-testid="nav-home"
            className={`flex flex-col items-center gap-0.5 p-1 ${activeTab === "home" ? 'text-[#009639]' : 'text-gray-500'}`}>
            <Home className="w-5 h-5" /><span className="text-[10px]">Home</span>
          </button>
          <button onClick={() => setActiveTab("feed")} data-testid="nav-feed"
            className={`flex flex-col items-center gap-0.5 p-1 ${activeTab === "feed" ? 'text-[#009639]' : 'text-gray-500'}`}>
            <MessageCircle className="w-5 h-5" /><span className="text-[10px]">Feed</span>
          </button>
          <button onClick={() => setActiveTab("challenges")} data-testid="nav-challenges"
            className={`flex flex-col items-center gap-0.5 p-1 ${activeTab === "challenges" ? 'text-[#009639]' : 'text-gray-500'}`}>
            <Users className="w-5 h-5" /><span className="text-[10px]">Challenges</span>
          </button>
          <button onClick={() => setActiveTab("training")} data-testid="nav-training"
            className={`flex flex-col items-center gap-0.5 p-1 ${activeTab === "training" ? 'text-[#009639]' : 'text-gray-500'}`}>
            <Dumbbell className="w-5 h-5" /><span className="text-[10px]">Training</span>
          </button>
          <button onClick={() => setActiveTab("races")} data-testid="nav-races"
            className={`flex flex-col items-center gap-0.5 p-1 ${activeTab === "races" ? 'text-[#FFD700]' : 'text-gray-500'}`}>
            <Flag className="w-5 h-5" /><span className="text-[10px]">Races</span>
          </button>
          <button onClick={() => setActiveTab("profile")} data-testid="nav-profile"
            className={`flex flex-col items-center gap-0.5 p-1 ${activeTab === "profile" ? 'text-[#009639]' : 'text-gray-500'}`}>
            <User className="w-5 h-5" /><span className="text-[10px]">Profile</span>
          </button>
        </div>
      </nav>

      {/* Weekly Goal Modal */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowGoalModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-[#FFD700]" /> Set Weekly Goal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400 text-xs">Miles per week</Label>
                <Input type="number" value={newGoal} onChange={e => setNewGoal(e.target.value)}
                  placeholder="e.g., 15" className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white text-lg" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowGoalModal(false)} variant="outline" className="flex-1 border-[#2D2D2D] text-gray-400">Cancel</Button>
                <Button onClick={updateWeeklyGoal} className="flex-1 bg-[#009639] hover:bg-[#007A2E]">Save Goal</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareWorkout && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5 text-[#009639]" /> Share Workout
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Workout Preview */}
              <div className="bg-[#0D0D0D] rounded-xl p-4 text-center">
                <div className="h-1 w-full flex rounded-full overflow-hidden mb-4">
                  <div className="flex-1 bg-[#C8102E]"></div>
                  <div className="flex-1 bg-[#FFD700]"></div>
                  <div className="flex-1 bg-[#009639]"></div>
                </div>
                {shareWorkout.photo && (
                  <img src={shareWorkout.photo} alt="Workout" className="w-full h-32 object-cover rounded-lg mb-3" />
                )}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-2xl">{getActivityIcon(shareWorkout.activity_type)}</span>
                  <p className="text-2xl font-bold text-white">{shareWorkout.distance.toFixed(2)} miles</p>
                </div>
                <p className="text-gray-400 text-sm">{formatTime(shareWorkout.duration)} • {formatPace(shareWorkout.avg_pace)}/mi</p>
                {(shareWorkout.elevation_gain > 0 || shareWorkout.steps > 0) && (
                  <p className="text-gray-500 text-xs mt-1">
                    {shareWorkout.elevation_gain > 0 && `⛰️ ${shareWorkout.elevation_gain}ft`}
                    {shareWorkout.elevation_gain > 0 && shareWorkout.steps > 0 && ' • '}
                    {shareWorkout.steps > 0 && `👟 ${shareWorkout.steps.toLocaleString()} steps`}
                  </p>
                )}
                <p className="text-[#009639] text-sm mt-2">staykoolrunning.co 🏃</p>
              </div>
              
              {/* Share Buttons */}
              <div className="space-y-2">
                <Button onClick={() => shareWorkoutImage(shareWorkout, 'native')} className="w-full bg-[#009639] hover:bg-[#007A2E]">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    onClick={() => shareWorkoutImage(shareWorkout, 'twitter')} 
                    variant="outline" 
                    className="border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    Twitter
                  </Button>
                  <Button 
                    onClick={() => shareWorkoutImage(shareWorkout, 'instagram')} 
                    variant="outline"
                    className="border-[#E4405F] text-[#E4405F] hover:bg-[#E4405F]/10"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    Instagram
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Route Replay Modal */}
      {showReplayModal && replayWorkout && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="route-replay-modal">
          {/* Header */}
          <div className="p-4 flex items-center justify-between bg-[#0D0D0D]">
            <div>
              <h2 className="text-white font-bold">Route Replay</h2>
              <p className="text-gray-400 text-xs">{new Date(replayWorkout.date).toLocaleDateString()}</p>
            </div>
            <button onClick={closeReplay} className="p-2 text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Map */}
          <div className="flex-1 relative">
            <MapContainer 
              center={[replayWorkout.route[0].lat, replayWorkout.route[0].lng]} 
              zoom={16} 
              className="w-full h-full" 
              zoomControl={false}
            >
              <RouteReplayMap 
                route={replayWorkout.route} 
                replayIndex={replayIndex} 
                isPlaying={isReplaying} 
              />
            </MapContainer>
            
            {/* Stay Kool Running Watermark */}
            <div className="absolute top-4 left-4 z-[1000] flex items-center gap-2 bg-[#0D0D0D]/90 backdrop-blur-sm rounded-lg px-3 py-2">
              <Logo size={32} />
              <div>
                <p className="text-white text-sm font-bold leading-tight">Stay Kool Running</p>
                <p className="text-[#FFD700] text-[10px] leading-tight">Every Mile Has Meaning</p>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="absolute bottom-4 left-4 right-4 bg-[#0D0D0D]/80 backdrop-blur rounded-lg p-3">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-400">{replayIndex + 1} / {replayWorkout.route.length} points</span>
                <span className="text-[#FFD700]">{replaySpeed}x speed</span>
              </div>
              <div className="h-2 bg-[#2D2D2D] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#009639] rounded-full transition-all duration-200" 
                  style={{ width: `${((replayIndex + 1) / replayWorkout.route.length) * 100}%` }} 
                />
              </div>
            </div>
          </div>
          
          {/* Controls */}
          <div className="p-4 bg-[#0D0D0D] border-t border-[#2D2D2D] safe-area-bottom">
            <div className="grid grid-cols-3 gap-4 mb-4 text-center">
              <div>
                <p className="text-2xl font-bold text-[#009639]">{replayWorkout.distance.toFixed(2)}</p>
                <p className="text-gray-400 text-xs">MILES</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{formatTime(replayWorkout.duration)}</p>
                <p className="text-gray-400 text-xs">TIME</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#FFD700]">{formatPace(replayWorkout.avg_pace)}</p>
                <p className="text-gray-400 text-xs">PACE</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4">
              <button onClick={resetReplay} className="w-12 h-12 rounded-full bg-[#2D2D2D] flex items-center justify-center">
                <RotateCcw className="w-5 h-5 text-gray-400" />
              </button>
              <button onClick={toggleReplay} className="w-16 h-16 rounded-full bg-[#009639] flex items-center justify-center shadow-lg" data-testid="replay-play-pause">
                {isReplaying ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white ml-1" />}
              </button>
              <button onClick={changeReplaySpeed} className="w-12 h-12 rounded-full bg-[#2D2D2D] flex items-center justify-center">
                <FastForward className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Challenge Modal */}
      {showChallengeModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowChallengeModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#009639]" /> Create Challenge
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400 text-xs">Challenge Name</Label>
                <Input 
                  value={newChallenge.name} 
                  onChange={e => setNewChallenge({...newChallenge, name: e.target.value})}
                  placeholder="e.g., January Mile Madness" 
                  className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white" 
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Description (optional)</Label>
                <Input 
                  value={newChallenge.description} 
                  onChange={e => setNewChallenge({...newChallenge, description: e.target.value})}
                  placeholder="Who can run the most miles?" 
                  className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white" 
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Challenge Type</Label>
                <select 
                  value={newChallenge.challenge_type}
                  onChange={e => setNewChallenge({...newChallenge, challenge_type: e.target.value})}
                  className="w-full mt-1 bg-[#0D0D0D] border border-[#2D2D2D] text-white rounded-md p-2"
                >
                  <option value="weekly_miles">Most Miles</option>
                  <option value="total_runs">Most Runs</option>
                  <option value="longest_run">Longest Single Run</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-400 text-xs">Start Date</Label>
                  <Input 
                    type="date" 
                    value={newChallenge.start_date}
                    onChange={e => setNewChallenge({...newChallenge, start_date: e.target.value})}
                    className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white" 
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">End Date</Label>
                  <Input 
                    type="date" 
                    value={newChallenge.end_date}
                    onChange={e => setNewChallenge({...newChallenge, end_date: e.target.value})}
                    className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white" 
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowChallengeModal(false)} variant="outline" className="flex-1 border-[#2D2D2D] text-gray-400">Cancel</Button>
                <Button onClick={createChallenge} className="flex-1 bg-[#009639] hover:bg-[#007A2E]">Create</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Join Challenge Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowJoinModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#009639]" /> Join Challenge
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400 text-xs">Invite Code</Label>
                <Input 
                  value={joinCode} 
                  onChange={e => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-character code" 
                  maxLength={6}
                  className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white text-center text-xl tracking-widest" 
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowJoinModal(false)} variant="outline" className="flex-1 border-[#2D2D2D] text-gray-400">Cancel</Button>
                <Button onClick={joinChallenge} className="flex-1 bg-[#009639] hover:bg-[#007A2E]">Join</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Challenge Detail Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" onClick={() => setSelectedChallenge(null)}>
          <div className="p-4 flex items-center justify-between bg-[#0D0D0D]">
            <div>
              <div className="flex items-center gap-2">
                {selectedChallenge.challenge_type === 'step_battle' && <Footprints className="w-5 h-5 text-[#FFD700]" />}
                <h2 className="text-white font-bold">{selectedChallenge.name}</h2>
              </div>
              <p className="text-gray-400 text-xs">
                {selectedChallenge.challenge_type === 'step_battle' 
                  ? `${challengeLeaderboard.length} competitors` 
                  : `${(selectedChallenge.participant_ids?.length || 0) + 1} participants`}
              </p>
            </div>
            <button onClick={() => setSelectedChallenge(null)} className="p-2 text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4" onClick={e => e.stopPropagation()}>
            {/* Step Battle: Update Your Steps */}
            {selectedChallenge.challenge_type === 'step_battle' && (
              <Card className="bg-gradient-to-r from-[#C8102E]/10 to-[#009639]/10 border-[#FFD700]/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold">Your Steps Today</h3>
                    <span className="text-[#FFD700] text-sm">Goal: {(selectedChallenge.target_value || 70000).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="number" 
                      placeholder="Enter your steps"
                      className="bg-[#0D0D0D] border-[#2D2D2D] text-white flex-1"
                      defaultValue={dailySteps}
                      id="stepInput"
                    />
                    <Button 
                      onClick={() => updateChallengeSteps(selectedChallenge.id, parseInt(document.getElementById('stepInput').value) || dailySteps)}
                      className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80"
                    >
                      Update
                    </Button>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">Sync from your step tracker or enter manually</p>
                </CardContent>
              </Card>
            )}

            {/* Invite Code */}
            <Card className="bg-[#1A1A1A] border-[#2D2D2D]">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Invite Code</p>
                  <p className="text-[#009639] text-xl font-bold tracking-widest">{selectedChallenge.invite_code}</p>
                </div>
                <Button onClick={() => copyInviteCode(selectedChallenge.invite_code)} size="sm" variant="outline" className="border-[#009639] text-[#009639]">
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <h3 className="text-white font-bold flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#FFD700]" />
              {selectedChallenge.challenge_type === 'step_battle' ? 'Step Battle Rankings' : 'Leaderboard'}
            </h3>
            <div className="space-y-2">
              {challengeLeaderboard.map((entry, index) => (
                <Card key={entry.user_id} className={`border-[#2D2D2D] ${entry.is_you ? 'ring-2 ring-[#009639]' : ''} ${index === 0 ? 'bg-[#FFD700]/10 border-[#FFD700]/30' : 'bg-[#1A1A1A]'}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      index === 0 ? 'bg-[#FFD700] text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-[#CD7F32] text-black' :
                      'bg-[#2D2D2D] text-gray-400'
                    }`}>
                      {entry.rank}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium flex items-center gap-2">
                        {entry.name}
                        {entry.is_you && <span className="text-[#009639] text-xs">(You)</span>}
                      </p>
                      {selectedChallenge.challenge_type === 'step_battle' ? (
                        <div className="w-full bg-[#2D2D2D] rounded-full h-2 mt-1">
                          <div 
                            className="bg-gradient-to-r from-[#C8102E] via-[#FFD700] to-[#009639] h-2 rounded-full transition-all"
                            style={{ width: `${Math.min((entry.steps / (selectedChallenge.target_value || 70000)) * 100, 100)}%` }}
                          />
                        </div>
                      ) : (
                        <p className="text-gray-500 text-xs">{entry.total_runs || 0} runs</p>
                      )}
                    </div>
                    <div className="text-right">
                      {selectedChallenge.challenge_type === 'step_battle' ? (
                        <>
                          <p className="text-[#FFD700] font-bold text-lg">{(entry.steps || 0).toLocaleString()}</p>
                          <p className="text-gray-500 text-xs">steps</p>
                        </>
                      ) : (
                        <>
                          <p className="text-[#009639] font-bold">{(entry.value || 0).toFixed(1)}</p>
                          <p className="text-gray-500 text-xs">
                            {selectedChallenge.challenge_type === 'weekly_miles' ? 'miles' : 
                             selectedChallenge.challenge_type === 'total_runs' ? 'runs' : 'mi'}
                          </p>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {challengeLeaderboard.length === 0 && (
                <p className="text-gray-500 text-center py-4">No participants yet. Share the invite code!</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step Battle Modal */}
      {showStepBattleModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowStepBattleModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#FFD700]/30 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b border-[#2D2D2D]">
              <CardTitle className="text-white flex items-center gap-2">
                <Footprints className="w-6 h-6 text-[#FFD700]" />
                Create Step Battle
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Challenge Name</label>
                <Input 
                  placeholder="e.g., Weekend Step War"
                  value={newStepBattle.name}
                  onChange={e => setNewStepBattle({...newStepBattle, name: e.target.value})}
                  className="bg-[#0D0D0D] border-[#2D2D2D] text-white"
                />
              </div>
              
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Opponent's Email (optional)</label>
                <Input 
                  type="email"
                  placeholder="friend@example.com"
                  value={newStepBattle.opponent_email}
                  onChange={e => setNewStepBattle({...newStepBattle, opponent_email: e.target.value})}
                  className="bg-[#0D0D0D] border-[#2D2D2D] text-white"
                />
                <p className="text-gray-500 text-xs mt-1">Leave blank to share invite code instead</p>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Daily Step Goal</label>
                <div className="grid grid-cols-3 gap-2">
                  {[5000, 10000, 15000].map(steps => (
                    <button
                      key={steps}
                      onClick={() => setNewStepBattle({...newStepBattle, target_steps: steps})}
                      className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                        newStepBattle.target_steps === steps 
                          ? 'bg-[#FFD700] text-black' 
                          : 'bg-[#2D2D2D] text-gray-400 hover:bg-[#3D3D3D]'
                      }`}
                    >
                      {steps.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Duration</label>
                <div className="grid grid-cols-4 gap-2">
                  {[3, 7, 14, 30].map(days => (
                    <button
                      key={days}
                      onClick={() => setNewStepBattle({...newStepBattle, days})}
                      className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                        newStepBattle.days === days 
                          ? 'bg-[#009639] text-white' 
                          : 'bg-[#2D2D2D] text-gray-400 hover:bg-[#3D3D3D]'
                      }`}
                    >
                      {days} days
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="bg-[#0D0D0D] rounded-lg p-3">
                <p className="text-gray-400 text-xs">Total Goal</p>
                <p className="text-[#FFD700] text-2xl font-bold">{(newStepBattle.target_steps * newStepBattle.days).toLocaleString()} steps</p>
                <p className="text-gray-500 text-xs">over {newStepBattle.days} days</p>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setShowStepBattleModal(false)} variant="outline" className="flex-1 border-[#2D2D2D] text-gray-400">
                  Cancel
                </Button>
                <Button onClick={createStepBattle} className="flex-1 bg-[#FFD700] text-black hover:bg-[#FFD700]/80">
                  Start Battle
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}


      {/* Weekly Step Battle Modal */}
      {showWeeklyBattleModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowWeeklyBattleModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#009639]/30 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b border-[#2D2D2D]">
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-[#009639]" />
                Weekly Step Battle
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <p className="text-gray-400 text-sm">Create a weekly challenge that automatically resets every Monday!</p>
              
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Challenge Name</label>
                <Input 
                  placeholder="e.g., Weekly Warriors"
                  value={newStepBattle.name}
                  onChange={e => setNewStepBattle({...newStepBattle, name: e.target.value})}
                  className="bg-[#0D0D0D] border-[#2D2D2D] text-white"
                />
              </div>
              
              <div>
                <label className="text-gray-400 text-sm mb-1 block">Daily Step Goal</label>
                <div className="grid grid-cols-4 gap-2">
                  {[5000, 8000, 10000, 15000].map(steps => (
                    <button
                      key={steps}
                      onClick={() => setNewStepBattle({...newStepBattle, target_steps: steps})}
                      className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                        newStepBattle.target_steps === steps 
                          ? 'bg-[#009639] text-white' 
                          : 'bg-[#2D2D2D] text-gray-400 hover:bg-[#3D3D3D]'
                      }`}
                    >
                      {(steps/1000)}K
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="bg-[#0D0D0D] rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs">Weekly Goal</p>
                    <p className="text-[#009639] text-2xl font-bold">{(newStepBattle.target_steps * 7).toLocaleString()}</p>
                    <p className="text-gray-500 text-xs">steps per week</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-xs">Starts</p>
                    <p className="text-white font-medium">Next Monday</p>
                    <p className="text-[#FFD700] text-xs">Auto-renews ♻️</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={() => setShowWeeklyBattleModal(false)} variant="outline" className="flex-1 border-[#2D2D2D] text-gray-400">
                  Cancel
                </Button>
                <Button onClick={createWeeklyBattle} className="flex-1 bg-[#009639] text-white hover:bg-[#009639]/80">
                  Create Weekly Battle
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col" onClick={() => setShowNotifications(false)}>
          <div className="bg-[#0D0D0D] p-4 flex items-center justify-between">
            <h2 className="text-white font-bold flex items-center gap-2">
              <Bell className="w-5 h-5" /> Notifications
            </h2>
            <button onClick={() => setShowNotifications(false)} className="p-2 text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3" onClick={e => e.stopPropagation()}>
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No notifications yet</p>
                <p className="text-gray-600 text-sm">Battle updates will appear here</p>
              </div>
            ) : (
              notifications.map(notif => (
                <Card 
                  key={notif.id} 
                  className={`border-[#2D2D2D] ${notif.read ? 'bg-[#1A1A1A]' : 'bg-[#1A1A1A] border-l-4 border-l-[#C8102E]'}`}
                  onClick={() => markNotificationRead(notif.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notif.type === 'battle_lead_change' ? 'bg-[#FFD700]/20' : 'bg-[#009639]/20'}`}>
                        {notif.type === 'battle_lead_change' ? (
                          <Trophy className="w-5 h-5 text-[#FFD700]" />
                        ) : (
                          <Bell className="w-5 h-5 text-[#009639]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{notif.title}</p>
                        <p className="text-gray-400 text-xs mt-1">{notif.body}</p>
                        <p className="text-gray-600 text-xs mt-2">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-[#C8102E]" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}




      {/* Training Plan Execution Modal */}
      {showPlanModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
          <div className="p-4 flex items-center justify-between bg-[#0D0D0D]">
            <div>
              <h2 className="text-white font-bold">{selectedPlan.name}</h2>
              <p className="text-gray-400 text-xs">Segment {activePlanSegment + 1} of {selectedPlan.segments.length}</p>
            </div>
            <button onClick={closePlanModal} className="p-2 text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            {/* Current Segment */}
            <div className="text-center mb-8">
              <p className={`text-6xl font-bold mb-2 ${
                selectedPlan.segments[activePlanSegment]?.type === 'run' ? 'text-[#C8102E]' :
                selectedPlan.segments[activePlanSegment]?.type === 'recovery' ? 'text-[#009639]' :
                'text-[#FFD700]'
              }`}>
                {selectedPlan.segments[activePlanSegment]?.type.toUpperCase()}
              </p>
              <p className="text-gray-400">{selectedPlan.segments[activePlanSegment]?.description}</p>
              {selectedPlan.segments[activePlanSegment]?.target_pace && (
                <p className="text-[#FFD700] mt-2">Target: {selectedPlan.segments[activePlanSegment].target_pace} min/mi</p>
              )}
            </div>

            {/* Timer */}
            <div className="text-center mb-8">
              <p className="text-7xl font-bold text-white tabular-nums">{formatTime(planTimer)}</p>
              <p className="text-gray-400 text-sm mt-2">
                / {formatTime(selectedPlan.segments[activePlanSegment]?.duration || 0)}
              </p>
            </div>

            {/* Progress */}
            <div className="w-full max-w-xs mb-8">
              <div className="h-3 bg-[#2D2D2D] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#009639] rounded-full transition-all duration-200" 
                  style={{ width: `${(planTimer / (selectedPlan.segments[activePlanSegment]?.duration || 1)) * 100}%` }} 
                />
              </div>
            </div>

            {/* Control */}
            <button 
              onClick={togglePlanExecution} 
              className={`w-24 h-24 rounded-full flex items-center justify-center shadow-lg ${isPlanRunning ? 'bg-[#FFD700]' : 'bg-[#009639]'}`}
            >
              {isPlanRunning ? <Pause className="w-12 h-12 text-black" /> : <Play className="w-12 h-12 text-white ml-2" />}
            </button>
          </div>

          {/* Segment List */}
          <div className="p-4 bg-[#0D0D0D] border-t border-[#2D2D2D] max-h-32 overflow-y-auto">
            <div className="flex gap-2">
              {selectedPlan.segments.map((segment, index) => (
                <div 
                  key={`segment-${segment.type}-${index}`}
                  className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
                    index === activePlanSegment ? 'bg-[#009639] text-white' :
                    index < activePlanSegment ? 'bg-[#2D2D2D] text-gray-500' :
                    'bg-[#1A1A1A] text-gray-400'
                  }`}
                >
                  {segment.type}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Plan Modal */}
      {showScheduleModal && planToSchedule && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowScheduleModal(false)}>
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#FFD700]" /> Schedule Training
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#0D0D0D] rounded-lg p-3">
                <p className="text-white font-bold">{planToSchedule.name}</p>
                <p className="text-gray-400 text-sm">{Math.floor(planToSchedule.total_duration / 60)} minutes</p>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Date</Label>
                <Input 
                  type="date" 
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white" 
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Reminder Time (optional)</Label>
                <Input 
                  type="time" 
                  value={scheduleTime}
                  onChange={e => setScheduleTime(e.target.value)}
                  className="mt-1 bg-[#0D0D0D] border-[#2D2D2D] text-white" 
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setShowScheduleModal(false)} variant="outline" className="flex-1 border-[#2D2D2D] text-gray-400">Cancel</Button>
                <Button onClick={schedulePlan} className="flex-1 bg-[#FFD700] hover:bg-[#E5C200] text-black">Schedule</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pace Target Modal */}
      {showPaceModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-white text-xl flex items-center justify-center gap-2">
                <Target className="w-6 h-6 text-[#FFD700]" />
                Set Target Pace
              </CardTitle>
              <p className="text-gray-400 text-sm mt-2">Get voice alerts if you fall behind your target pace</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-400 mb-2 block">Target Pace (min:sec per mile)</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="text"
                    placeholder="9:00"
                    value={tempTargetPace}
                    onChange={(e) => setTempTargetPace(e.target.value)}
                    className="bg-[#0D0D0D] border-[#2D2D2D] text-white text-center text-2xl font-bold"
                    data-testid="pace-target-input"
                  />
                  <span className="text-gray-400 text-lg">/mi</span>
                </div>
                <p className="text-gray-500 text-xs mt-2">Examples: 8:00 (fast), 9:30 (moderate), 12:00 (easy)</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowPaceModal(false)} 
                  variant="outline" 
                  className="flex-1 border-[#2D2D2D] text-gray-400"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    const pace = parsePaceString(tempTargetPace);
                    if (pace && pace > 0 && pace < 30) {
                      setTargetPace(pace);
                      toast.success(`Target pace set to ${formatPace(pace)}/mi`);
                      setShowPaceModal(false);
                    } else {
                      toast.error("Invalid pace format. Use mm:ss (e.g., 9:30)");
                    }
                  }} 
                  className="flex-1 bg-[#FFD700] hover:bg-[#E5C200] text-black"
                >
                  Set Target
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Type Selection Modal */}
      {showActivityTypeModal && pendingWorkoutData && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1A1A1A] border-[#2D2D2D] w-full max-w-sm" data-testid="activity-type-modal">
            <CardHeader className="pb-2 text-center">
              <div className="h-1 w-full flex rounded-full overflow-hidden mb-4">
                <div className="flex-1 bg-[#C8102E]"></div>
                <div className="flex-1 bg-[#FFD700]"></div>
                <div className="flex-1 bg-[#009639]"></div>
              </div>
              <CardTitle className="text-white text-xl">Great Workout! 🎉</CardTitle>
              <p className="text-gray-400 text-sm mt-2">What type of activity was this?</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Workout Summary */}
              <div className="bg-[#0D0D0D] rounded-xl p-4">
                <div className="grid grid-cols-3 gap-3 text-center mb-3">
                  <div>
                    <p className="text-xl font-bold text-[#009639]">{pendingWorkoutData.distance.toFixed(2)}</p>
                    <p className="text-gray-500 text-[10px]">MILES</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">{formatTime(pendingWorkoutData.duration)}</p>
                    <p className="text-gray-500 text-[10px]">TIME</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#FFD700]">{formatPace(pendingWorkoutData.avg_pace)}</p>
                    <p className="text-gray-500 text-[10px]">PACE</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center border-t border-[#2D2D2D] pt-3">
                  <div>
                    <p className="text-lg font-bold text-[#C8102E]">{pendingWorkoutData.steps?.toLocaleString() || 0}</p>
                    <p className="text-gray-500 text-[10px]">STEPS</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#009639] flex items-center justify-center gap-1">
                      <ArrowUp className="w-3 h-3" />{pendingWorkoutData.elevation_gain || 0}
                    </p>
                    <p className="text-gray-500 text-[10px]">ELEV FT</p>
                  </div>
                </div>
              </div>
              
              {/* Photo Upload */}
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={photoInputRef}
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                {workoutPhoto ? (
                  <div className="relative rounded-xl overflow-hidden">
                    <img src={workoutPhoto} alt="Workout" className="w-full h-32 object-cover" />
                    <button
                      onClick={() => setWorkoutPhoto(null)}
                      className="absolute top-2 right-2 bg-[#C8102E] p-1.5 rounded-full"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full py-3 border-2 border-dashed border-[#2D2D2D] rounded-xl text-gray-400 hover:border-[#009639] hover:text-[#009639] transition-colors flex items-center justify-center gap-2"
                  >
                    <Camera className="w-5 h-5" />
                    <span className="text-sm">Add Photo</span>
                  </button>
                )}
              </div>
              
              {/* Workout Notes */}
              <div>
                <textarea
                  placeholder="Add notes (weather, how you felt, race conditions...)"
                  value={workoutNotes}
                  onChange={(e) => setWorkoutNotes(e.target.value)}
                  className="w-full bg-[#0D0D0D] border border-[#2D2D2D] rounded-xl p-3 text-white text-sm placeholder-gray-500 resize-none focus:outline-none focus:border-[#009639]"
                  rows={2}
                  data-testid="workout-notes-input"
                />
              </div>
              
              {/* Activity Type Selection */}
              <div className="grid grid-cols-2 gap-3">
                {ACTIVITY_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedActivityType(type.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedActivityType === type.id 
                        ? 'border-[#009639] bg-[#009639]/20' 
                        : 'border-[#2D2D2D] bg-[#0D0D0D]'
                    }`}
                    data-testid={`activity-type-${type.id}`}
                  >
                    <span className="text-3xl block mb-1">{type.icon}</span>
                    <span className={`text-sm font-medium ${selectedActivityType === type.id ? 'text-[#009639]' : 'text-white'}`}>
                      {type.name}
                    </span>
                  </button>
                ))}
              </div>
              
              <Button 
                onClick={() => saveWorkoutWithType(selectedActivityType)} 
                className="w-full bg-[#009639] hover:bg-[#007A2E] h-12 text-lg"
                data-testid="save-workout-button"
              >
                Save {ACTIVITY_TYPES.find(t => t.id === selectedActivityType)?.name}
              </Button>
              
              <button 
                onClick={() => {
                  setShowActivityTypeModal(false);
                  setPendingWorkoutData(null);
                  setIsTracking(false);
                  setTrackingData({ route: [], distance: 0, duration: 0, pace: 0, startTime: null });
                  setCurrentPosition(null);
                  setWorkoutPhoto(null);
                  toast.info("Workout discarded");
                }}
                className="w-full text-center text-gray-500 text-sm py-2"
              >
                Discard Workout
              </button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default App;
